/**
 * @file        dcp/src/dcp-client/worker/supervisor2/index.js
 *              Code managing sandboxes, tasks, jobs, and slices within in a DCP Worker.
 * @author      Wes Garland, wes@distributive.network
 *              Paul, paul@distributive.network
 * @date        Dec 2020
 *              June 2022, Jan-April 2023
 * @module supervisor
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */

//
// NOTE: How to easily turn on tracing.
// 1) For minimal tracing
//    DCP_DEBUG=supex ProgramThatCallsSupervisor
// 2) For medium tracing
//    DCP_DEBUG=sup ProgramThatCallsSupervisor
//    or
//    DCP_DEBUG=sup,supex ProgramThatCallsSupervisor// 3) For full tracing
//    DCP_DEBUG=worker,supervisor ProgramThatCallsSupervisor
// 4) For insane tracing
//    DCP_DEBUG=*,* ProgramThatCallsSupervisor
//

/*
 * initial      ready                                 stopping                stopped                   paused           broken
 * |-- ctor ----------------------------------------------------------------------------------------------------------------->
 * |-- work ----------------------------------------------------------------------------------------------------------------->
 * |-- work ----->
 *               |-- work --------------------------------------------------------------------------------------------------->
 *               |-- pause -------------------------------------------------------------------------------->
 *               <-- unpause ------------------------------------------------------------------------------|
 *               |-- stopWork ----------------------------->
 *                                                         |-- stopAndShutdown --->
 * <-- work ----------------------------------------------------------------------|
 * <-- work -----------------------------------------------------------------------------------------------|
 * |-- stopWork ------------------------------------------->
 *               |-- stopWork ----------------------------->
 *                                                         <-- stopWork -----------------------------------|
 *                                                         <-- stopWork -----------------------------------------------------|
 */
// @ts-check
'use strict';

const debugging = require('dcp/debugging').scope('worker');
const DCP_ENV = require('dcp/common/dcp-env');
const { assert } = require('dcp/common/dcp-assert');
const dcp_timers = require('dcp/common/dcp-timers');
const EventEmitter = require('events');
const { DCPError } = require('dcp/common/dcp-error');
const { RingBuffer } = require('dcp/utils');
const { Synchronizer } = require('dcp/common/concurrency');
const { JobManager } = require('./job-manager');
const { Sandbox, SandboxError } = require('./sandbox2');
const { OriginAccessManager } = require('dcp/dcp-client/worker/origin-access-manager');
const { DcpURL } = require('dcp/common/dcp-url');
const { justFetch } = require('dcp/utils/just-fetch');
const { FetchState } = require('dcp/utils/fetchState');
const { serializeJobValue } = require('dcp/dcp-client/job-values');
const utils = require('dcp/utils');
const wd = require('dcp/utils/worker-and-daemons');
const capabilities = require('dcp/common/capabilities');
const kvin = require('kvin');
const management = require('../management');
const wallet = require('dcp/dcp-client/wallet');
const { recoverAddressfromSignature, Address } = require('dcp/dcp-client/wallet/eth');
const identity = require('dcp/dcp-client/identity');

const { Diagnostics } = require('./diagnostics');
const { ModuleCache } = require('./module-cache');
const { Promise_any } = require('./promise_any');
const { ProtocolManager } = require('./protocol-manager');
const { EvaluatorManager } = require('./evaluator-manager');
const { DelayManager } = require('./delay-manager');
const { SupervisorOptions } = require('./options');
const common = require('./common');
const { debugBuild, selectiveDebug, selectiveDebug2, selectiveSupEx } = common;
const { Quanta } = require('./quanta');
const { generateTaskRequestWorkerComputeGroups } = require('dcp/dcp-client/compute-groups/authorization');
const { SandboxBackoffManager } = require('./sandbox-backoff-manager');

/** @typedef {string} opaqueId */ // 22 character base64 string
/** @typedef {import('./slice2').Slice} Slice                             */
/** @typedef {import('dcp/utils/jsdoc-types').Auth} Auth                  */
/** @typedef {import('dcp/utils/jsdoc-types').Body} Body                  */
/** @typedef {import('./sandbox2').SandboxHandle} SandboxHandle           */
/** @typedef {import('dcp/utils/jsdoc-types').TDPayload} TDPayload        */
/** @typedef {import('dcp/utils/jsdoc-types').Signature} Signature        */
/** @typedef {import('dcp/utils/jsdoc-types').SliceMessage} SliceMessage  */
/** @typedef {import('dcp/protocol/connection').Connection} Connection    */
/** @typedef {import('dcp/dcp-client/worker/index').DistributiveWorker} DistributiveWorker */

//
// Index to functionality -- search for '_Idx' to toggle through the index.
//
// 1)  class Supervisor
// 2)  work, checkCapabilities, constructReturnAllSlicesMessage
// 3)  jobEmit, error, warning, showSlices
// 4)  destroyAllJobs, stopAndShutdown, stopWork, purgeJob, rejectJob
// 5)  roundRobinSlices, makeJobSelectionCursor, handleSandboxWorkError, handleFailedSlice
// 6)  returnSlices, returnSlice, directReturnSlice, tabSuspended, checkForTabSuspension, sendReturnAllSlicesMessage, sendReturnAllSlicesBeacon
// 7)  repoMan, predictLoad(viz., clairvoyance), waitUntilWorkIsReady
// 8)  availableSandboxSpace, fetchTask, createTDRequest, addTaskToWorkload, fetchFromTD, clearUnusedJobs
// 9)  createSandbox, returnSandbox, hookUpSandboxListeners, pruneSandboxes
// 10) recordResult, sendToResultSubmitter, sendResultToRemote
// 11) handleWorkReject
//

// _Idx
//
// class Supervisor
//

/**
 * Supervisor constructor
 *
 * A supervisor manages the communication with the scheduler, manages sandboxes, and
 * decides which workload should be sent to which sandboxes when.
 *
 * Possible states: 'initial', 'ready', 'stopping', 'stopped', 'broken'
 * Start state:
 * - initial
 *
 * Intermediate states:
 * - ready
 * - stopping
 *
 * Terminal states:
 * - stopped
 * - broken
 *
 * Valid transitions:
 * - initial -> ready -> stopping -> stopped
 * - initial -> broken
 */
class Supervisor extends EventEmitter
{
  /**
   * @constructor
   * @param {DistributiveWorker} worker - parent worker
   */
  constructor (worker, SandboxConstructor)
  {
    super({ captureRejections: false });

    /** @type {DistributiveWorker} */
    this.worker = worker;
    /** @type {SupervisorOptions} */
    this.options = new SupervisorOptions(this.worker);
    this.SandboxConstructor = SandboxConstructor;
    this.recentWarnings = new Map();

    selectiveDebug() && console.debug('Supervisor: cores.cpu, cores.gpu, maxSandboxes', this.options.cores?.cpu, this.options.cores?.gpu, this.options.maxSandboxes);

    /** @type {ModuleCache} */
    this.moduleCache = new ModuleCache(this);

    // Manage delays and exponential backoff.
    this.delayManager = new DelayManager(this, this.options.defaultDelayIncrementMs);
    this.fetchBackoffs = {};

    /* See https://distributive.atlassian.net/browse/DCP-3175 */
    /** @type {OriginAccessManager} */
    this.originManager = new OriginAccessManager(this.options.allowOrigins, this.options.allowOriginsCurrentScheduler);

    /** @type {ProtocolManager} */
    this.dcp4 = new ProtocolManager(this);

    /** @type {common.DebuggingTools} */
    this.dbg = new common.DebuggingTools(this);

    // Turn on for max speed debugging.
    // if (false)
    // {
    //   this.delayManager.delayScaler = 0.05;
    //   this.options.watchdogInterval = 0.25;
    // }

    /** @type { Synchronizer } */
    this.state = new Synchronizer('initial', [ 'initial', 'ready', 'stopping', 'stopped', 'paused', 'broken' ]);
    /** @type {Object<string, JobManager>} */
    this.jobMap = {}; // jobId => jobManager

    /** @type {JobManager[]} */
    this.jobManagerInventory = common.InventoryArray('jobManagers');
    /** @type {Sandbox[]} */
    this.sandboxInventory = []; // All sandboxes that are being used by the job managers. Makes sure we don't lose sandboxes.
    /** @type {{ next: cbNext, commit: cbCommit, useGPU: cbUseGPU }} */
    this.cursor = null;
    /**  @type {FetchState} */
    this.fetchState = null;
    /**
     * this.quanta.localTime := fixed-up EMA times series of CPUTime (or GPUTime) over all jobs.
     * this.quanta.globalTime := fixed-up EMA times series of sliceCPUTime (or sliceGPUTime) over all jobs.
     * this.quanta.defaultQuanta := fixed-up ratio: this.quanta.localTime / this.quanta.globalTime
     * @type {Quanta}
     */
    this.quanta = new Quanta(this);

    /**
     * Evaluator down management.
     * @type {EvaluatorManager}
     **/
    this.evaluator = new EvaluatorManager();

    /** @type (SandboxBackoffManager) */
    this.sandboxBackoffManager = new SandboxBackoffManager(this);

    // There are 2 kinds of barriers.
    // 1) fetchTaskBarrier is a barrier for the task fetching from task distributor path.
    // 2) roundRobinBarrier is a barrier for the slice execution path.
    /** @type {boolean} */
    this.fetchTaskBarrier = false;
    /** @type {boolean} */
    this.roundRobinBarrier = false;
    /** @type {string[]} */
    this.unresolvedJobs = [];
    /**
     *  An N-slot ring buffer of job ids. Stores all jobs that have been rejected by the worker
     *  Required for the implementation of work.reject
     *  @type {RingBuffer}
     */
    this.rejectedJobs = new RingBuffer(200); //  N = 200 should be more than enough.
    /**
     *  An N-slot ring buffer of job ids. Stores all jobs that have had no more than 1 slice run in the ring buffer.
     *  Required for the implementation of discrete jobs
     *  @type {RingBuffer}
     */
    this.discreteJobs = new RingBuffer(200); //  N = 200 should be more than enough.
    /**
     * When true we await waitUntilWorkIsReady until at least 1 job is ready with at least 1 ready slice.
     * waitUntilWorkIsReady
     * @type {boolean}
     */
    this.waitForWork = true;
    /**
     * Last repoMan time stamp.
     * @type {number}
     */
    this.lastRepoMan = Date.now();
    /**
     * General time stamp.
     * @type {number}
     */
    this.lastTime = Date.now();
    /**
     * Last time a Soteria request was sent to Task Distributor.
     * @type {number}
     */
    this.lastSoteriaRequest = 0;
    /**
     * Run once at startup.
     * @type {boolean}
     */
    this.runOnceAtStartup = true;
    /**
     * Fetch started time stamp.
     * @type {number}
     */
    this.fetchTaskStarted = 0;
    /**
     * Sequence number for fetch task
     * @type {number}
     */
    this.fetchSeq = 0;
    /**
     * The capabilities of a random sandbox.
     * @todo XXXpfr Re-work this once fetchTask uses the capabilities of every sandbox to fetch slices.
     * @type {object}
     */
    this.capabilities = null;
    /**
     * Results of the webGPU benchmark (only run if the worker capabilities includes webGPU).
     * @type {number}
     */
    this.webGPUBenchmarkMs = -1;

    /**
     * The worktimes supported by the client environment.
     * The default of undefined means that in requestTask, worktimes will take on the op-router default specified.
     * @type {Array<{ name: string, version: String }>}
     */
    this.worktimes = undefined;
    /**
     * When this.sliceTiming is set to be true, it displays the timings of a every slice
     * slice['executionDelta'] = timespan of execution in sandbox
     * slice['resultDelta'] = timespan of when sandbox finishes executing until recordResult completes.
     * @type {boolean}
     */
    this.sliceTiming = false;
    /**
     * Timestamp which represents the last time the checkForTabSuspension method was invoked.
     * @type {number}
     */
    this.lastSuspensionDetectionCheck = 0;
    /**
     * When true, supress ENOPROGRESS for a few minutes.
     * @type {boolean}
     */
    this.detectedTabSuspended = false;
    /**
     * Timer handle for timer that resets this.detectedTabSuspended back to false.
     * @type {*}
     */
    this.detectedTabTimer = null;
    /**
     * Pause scheduling a GPU slice until Date.now() > this.pauseGPUSchedulingUntil.
     * @type {number}
     */
    this.pauseGPUSchedulingUntil = 0;
     /**
     * The number of GPU slices working, or will be working very soon.
     * @type {number}
     */
    this.existingGPUBarriers = 0;

    if (DCP_ENV.isBrowserPlatform)
      window.addEventListener('pagehide', () => this.sendReturnAllSlicesBeacon());
    /**
     * History of roundRobinSlices execution.
     * Used for introspection during debugging and tracing.
     */
    this.rrsHistory = {
      workerId: this.workerId,
      maxSandboxes: this.options.maxSandboxes,
      cores: this.cores,
      lastSuccess: {},
      rb: new RingBuffer(200),
    };

    this.unrecordedEarnings = 0;
  }

  //
  // Compatibility layer between Sup1, Sup2 and the Sup interface exposed by Worker.
  //
  /**
   * Get all sandboxes.
   * @type {Sandbox[]}
   */
  get sandboxes () { return this.sandboxInventory.filter((sandbox) => !sandbox.isTerminated); }
  /**
   * Get all working sandboxes.
   * @type {Sandbox[]}
   */
  get workingSandboxes () { return this.sandboxInventory.filter((sandbox) => sandbox.isWorking); }
  /**
   * Get the number of working sandboxes.
   * @type {number}
   */
  get workingSandboxCount () { return this.workingSandboxes.length; }
  /**
   * Get all slices over all jobs..
   * @type {Slice[]}
   */
  get slices () {
    const slices = [];
    this.jobManagerInventory.forEach((jobManager) => { slices.push(...jobManager.sliceInventory); });
    return slices;
  }
  /**
   * Get all queued slices over all jobs..
   * @type {Slice[]}
   */
  get queuedSlices () {
    const slices = [];
    this.jobManagerInventory.forEach((jobManager) => { slices.push(...jobManager.queuedSlices); });
    return slices;
  }
  /**
   * Get all queued slices over all jobs..
   * @type {Slice[]}
   */
  get workingSlices () {
    const slices = [];
    this.jobManagerInventory.forEach((jobManager) => { slices.push(...jobManager.workingSlices); });
    return slices;
  }
  /** @type {opaqueId} */
  get workerId ()   { return this.worker.id; }
   /** @type {string} */
  get version () { return '2.0.0'; }

  //
  // Miscellaneous properties.
  //
  /**
   * Dynamic maxWorkingCores.
   * The maximum number of cores that can be executing slices. Slices are scheduled
   * using density. E.g. suppose a job has GPUDensity is 0 and CPUDensity is 0.5,
   * then 2 slices of this job can be scheduled on a single core.
   * @type {number}
   */
  get maxWorkingCores () { return Math.min(this.options.cores.cpu, this.options.maxSandboxes); }
  /**
   * The # of GPUs available for scheduling.
   * @type {number}
   */
  get usableGPUCount () { return Math.ceil(this.maxWorkingGPUs); }
  /**
   * When true, more than 1 GPU slice can execute concurrently.
   * @type {boolean}
   */
  get concurrentGPUSlices () { return true; }
  /**
   * Dynamic maxWorkingGPUs.
   * The maximum number of GPUs that can be executing slices. Slices are scheduled
   * using density. E.g. suppose a job has GPUDensity is 0.5 and CPUDensity is 0.5,
   * then 2 slices of this job can be scheduled on a single GPU core and a single CPU core.
   * @type {number}
   */
  get maxWorkingGPUs () { return this.options.cores.gpu; }
  /**
   * Check whether the sandbox capabilities supports GPU compute.
   * @type {boolean}
   */
  get supportsGPU () { return Boolean(this.capabilities?.environment?.webgpu) ?? false; }
  /**
   * Indicates whether supervisor is in the ready state.
   * It's possible to be ready, yet cannot create sandboxes because the evaulator is down.
   * @type {boolean}
   */
  get isReady () { return this.state.is('ready'); }
  /**
   * Indicates whether supervisor is ready to execute slices.
   * @type {boolean}
   */
  get isReadyForWork () { return this.state.is('ready') && !this.evaluator.reallyDown; }
  /**
   * The # of sandboxes not being used.
   * @type {number}
   */
  get unusedSandboxCount () { return this.options.maxSandboxes - this.workingSliceCount; }
  /**
   * The unused amount of CPU density in the cores.
   * @type {number}
   */
  get unusedCoreSpace () { return this.maxWorkingCores - this.workingSliceDensity; }
  /** @type {number} */
  get workingSliceDensity ()
  {
    let density = 0;
    for (const jobMan of this.jobManagerInventory)
      density += jobMan.workingSliceDensity;
    return density;
  }
  /** @type {number} */
  get workingGPUDensity ()
  {
    let density = 0;
    for (const jobMan of this.jobManagerInventory)
      if (jobMan.hasGPU)
        density += jobMan.workingGPUDensity;
    return density;
  }
  /** @type {number} */
  get workingSliceCount ()
  {
    let count = 0;
    for (const jobMan of this.jobManagerInventory)
      count += jobMan.workingSliceCount;
    return count;
  }
  /**
   * While a bit redundant, the concept of cores is so basic that we need an accessor.
   * Will prevent future mistakes.
   * @type {{ cpu: number, gpu: number }}
   */
  get cores () { return this.options.cores; }
  /**
   * Concise view of worker state.
   */
  get workerState ()
  {
    const state = {
      status:               this.state.toString(),
      workerId:             this.worker.id,
      maxSandboxes:         this.options.maxSandboxes,
      unusedSandboxes:      this.unusedSandboxCount,
      maxCPUCores:          this.cores.cpu,
      maxGPUCores:          this.cores.gpu,
      supportsGPU:          this.supportsGPU,
      unusedCPU:            this.unusedCoreSpace,
      tdbarrier:            this.fetchTaskBarrier,
      rrbarrier:            this.roundRobinBarrier,
      evalDown:             this.evaluator.reallyDown,
      leavePubCG:           this.options.leaveGlobalGroup || this.worker.config.leavePublicGroup /* deprecated */,
      minWage:              this.options.minimumWage,
      jobStates:            this.jobManagerInventory.map((jobMan) => jobMan.jobState),
    };
    return state;
  }

  //
  // _Idx
  //
  // work, checkCapabilities, constructReturnAllSlicesMessage
  //

  /**
   * Set up sandboxes and interval timers, then start to search for work.
   * Called in Worker.start().
   * Initial entry point after Worker constructor.
   * We need to start searching for work here to allow starting and stopping a worker.
   */
  work ()
  {
    /* Provide opportunity for calling code to hook ready/error events. */
    dcp_timers.setImmediate(() => {
      try
      {
        // [ 'initial', 'ready', 'stopping', 'stopped', 'paused', 'broken' ]
        if (this.state.isNot('initial'))
        {
          if (this.state.in(['ready', 'stopping']))
          {
            this.warning(`Supervisor.work was called when supervisor is already ${this.state.valueOf()}.`, 'Please either wait and try again or restart worker.');
            return;
          }
          else if (this.state.is('broken'))
          {
            this.warning("Cannot call Supervisor.work when supervisor is in a 'broken' state. Please restart worker.");
            return;
          }
          this.state.set(['stopped', 'paused'], 'initial');
        }

        // Start up the connections.
        this.dcp4.instantiateAllConnections();
        this.evaluator.initialize();
        // Construct returnAllSlicesMessage
        this.constructReturnAllSlicesMessage();

        /**
         * Watchdog: fetchTask-driven interval timer.
         * This is what drives the worker to always fetch slices.
         * @type {*}
         */
        this.watchdogTimer = dcp_timers.setInterval(() => {
            selectiveDebug2() && console.debug('Watchdog fetchTask', Date.now() - this.lastTime, this.sandboxInventory.map((sbx) => sbx.identifier));
            this.fetchTask().catch ((error) => {
              // fetchTask is allowed to fail w/o hurting the worker.
              selectiveSupEx() && console.debug('Watchdog fetchTask failed:', error);
            });
        }, this.options.watchdogInterval);
        this.watchdogTimer.unref();

        if (DCP_ENV.isBrowserPlatform)
        {
          /**
           * Interval timer used to detect when the tab becomes suspended
           * @type {*}
           */
          this.suspensionDetectionInterval = dcp_timers.setInterval(() => this.checkForTabSuspension(), this.options.detectSuspensionIntervalMs);
          this.suspensionDetectionInterval.unref();
        }

        if (selectiveSupEx())
        {
          /**
           * Debugging helper interval timer:
           * @type {*}
           */
          this.sliceDebuggingTimer = dcp_timers.setInterval(() => {
            console.debug(`WorkerState(${new Date()}):`, utils.stringify(this.workerState, 8192, 2));
          }, 45 * 1000).unref();
        }

        this.state.set('initial', 'ready');

        // Don't wait for watchdog.
        this.fetchTask().catch (async (error) => {
          // fetchTask is allowed to fail w/o hurting the worker.
          selectiveSupEx() && console.debug('Initial fetchTask failed:', error);
        });
      }
      catch(error)
      {
        this.worker.emit(error);
        if (this.state.is('initial')) this.state.set('initial', 'broken');
        else if (!this.state.is('broken')) this.state.set('ready', 'broken');
        this.worker.stop(true).finally(() => { throw error; });
      }
    });
  }

  /** Construct capabilities when necessary. */
  async checkCapabilities (sandbox)
  {
    /**
     * Assign the capabilities of one the sandboxes before fetching slices from the scheduler.
     * @todo XXXpfr Re-work this once fetchTask uses the capabilities of every sandbox to fetch slices.
     */
    if (!sandbox.capabilities && !sandbox.isTerminated)
      throw new Error(`Sandbox ${sandbox.id} with state ${sandbox.state.valueOf()} does not have capabilities.`);

    this.capabilities = sandbox.capabilities;
    if (!this.capabilities.hasOwnProperty('engine'))
      this.capabilities.engine = {};
    this.capabilities.engine.browser = DCP_ENV.isBrowserPlatform;
    this.capabilities.KVINv9 = 1;
    // @Todo: detect v8 and spidermonkey

    if (this.options.runWebGPUBenchmark && this.capabilities.environment?.webgpu)
    {
      const benchmarkRes = await sandbox.eval(require('./webgpu-benchmark.js').benchmark, 'webgpu-benchmark.js');
      selectiveSupEx() && console.debug('webgpu benchmark results', benchmarkRes);
      if (benchmarkRes && benchmarkRes.data !== 'Unable to run benchmark')
        this.webGPUBenchmarkMs = benchmarkRes.data;
    }

    debugging('supervisor') && console.debug(`Supervisor.checkCapabilities computed(${Date.now() - this.lastTime})`, utils.stringify(this.capabilities, 1024, 2));
  }

  /**
   * Construct returnAllSlicesMessage in the work() method (called in worker.start()).
   * Some scenarios set workerId right after creating a DistributiveWorker, and we want to use
   * that workerId in the construction of returnAllSlicesMessage.
   *
   * The returnAllSlicesMessage when sent to TD causes all rows in activeSlices wrt current worker,
   * to be returned to scheduler.
   *
   * @returns {Promise<*>}
   */
  async constructReturnAllSlicesMessage ()
  {
    const ks = identity.get();
    return ks.makeSignedMessage({ id: this.workerId }).then((msg) => {
      return this.returnAllSlicesMessage = msg;
    });
  }

  // _Idx
  //
  // jobEmit, error, warning, showSlices
  //

  /**
   * Safe event emitter on slice.jobHandle.
   * @param {Slice}   slice
   * @param {string}  event
   * @param  {...any} args
   */
  jobEmit(slice, event, ...args)
  {
    slice.jobHandle.emit(event, ...args);
  }

  //
  // Rules:
  // (1) Never modify an instance of Error.
  // (2) Never modify a property of any error-like object.
  //

  /**
   * Certain socket errors may optionally not be reported.
   * @param {*} error
   * @returns (boolean) -- true when error is reportable
   */
  static isReportable (error)
  {
    switch(error?.code)
    {
      case 'ESANDBOX':      /* sandbox end event has fired */
      case 'ECONNREFUSED':  /* remote not listening - evaluator not running? */
      case 'ECONNRESET':    /* RST packet sent: remote closed - throttled? */
      case 'ECONNABORTED':  /* unexpectedly aborted by the other side */
      case 'EPIPE':
      case 'ETIMEDOUT':
        debugging() && console.debug('Non-reportable error:', error)
        return false;
      default:
        return true;
    }
  }

  /**
   * When isWorkerError, coerce error to an instance of WorkerError.
   * @param {boolean} isWorkerError
   * @param {Error} error
   * @oaram {string} [code]
   * @returns {Error} error
   */
  static coerceError(isWorkerError, error, code)
  {
    /** @todo Fix DCP-4648 or else DistributiveWorker does not properly resolve at global scope. */
    const { DistributiveWorker } = require('../index');
    const err = isWorkerError ? DistributiveWorker.createWorkerError(error) : error;
    if (code)
      err['code'] = code;
    return err;
  }

  /**
   * General error feedback to the DistributiveWorker API consumer
   * @param {string}  message
   * @param {*}       coreError - could be Array<Error>|Error|string|ErrorPayload
   * @param {boolean} disectCoreError
   * @param {boolean} displayTopOfStack
   * @param {boolean} isWorkerError
   */
  #emitError (message, coreError, disectCoreError, displayTopOfStack, isWorkerError)
  {
    if (!Supervisor.isReportable(coreError))
      return;
    let code;
    if (disectCoreError && coreError?.['message'])
    {
      code = coreError.code;
      coreError = coreError.message;
    }
    const error = Diagnostics.error(message, coreError, displayTopOfStack);
    if (error)
    {
      if (Array.isArray(error))
        error.forEach((e) => this.worker.emit('error', Supervisor.coerceError(isWorkerError, e, code)));
      else
        this.worker.emit('error', Supervisor.coerceError(isWorkerError, error, code));
    }
  }

  /**
   * NON-FATAL Error feedback to the DistributiveWorker API consumer
   * Forms:
   *     emitWorkerError(message, coreError[, false, true]) -- where coreError is Error-like
   *     emitWorkerError(message, additionalInfo[, false, true]) -- where additionalInfo is convertible to a string
   * Note:
   *     dcpError=this.emitWorkerError(msg, error, disectCoreError, false)
   * has dcpError.stack === `DCPError: ${msg}\nRethrow ${error.stack}`
   * When disectCoreError is true, error.info=coreError.message, error.code=coreError.code.
   * @param {string}  message
   * @param {*}       [coreError=null] - could be Array<Error>|Error|string|ErrorPayload
   * @param {boolean} [disectCoreError=false]
   * @param {boolean} [displayTopOfStack=false]
   */
  emitWorkerError (message, coreError = null, disectCoreError = false, displayTopOfStack = false)
  {
    return this.#emitError (message, coreError, disectCoreError, displayTopOfStack, /*isWorkerError*/ true);
  }

  /**
   * FATAL Error feedback to the DistributiveWorker API consumer
   * Forms:
   *     emitFatalError(message, coreError[, false, true]) -- where coreError is Error-like
   *     emitFatalError(message, additionalInfo[, false, true]) -- where additionalInfo is convertible to a string
   * Note:
   *     dcpError=this.emitFatalError(msg, error, disectCoreError, false)
   * has dcpError.stack === `DCPError: ${msg}\nRethrow ${error.stack}`
   * When disectCoreError is true, error.info=coreError.message, error.code=coreError.code.
   * @param {string}  message
   * @param {*}       [coreError=null] - could be Array<Error>|Error|string|ErrorPayload
   * @param {boolean} [disectCoreError=false]
   * @param {boolean} [displayTopOfStack=false]
   */
  emitFatalError (message, coreError = null, disectCoreError = false, displayTopOfStack = false)
  {
    return this.#emitError(message, coreError, disectCoreError, displayTopOfStack, /*isWorkerError*/ false);
  }

  /**
   * Emit general error as directly as possible to the worker error event.
   * @param {{ message: string }|string} error
   * @param {string}  info - addition information
   * @param {boolean} isWorkerError
   */
  #directError (error, info, isWorkerError)
  {
    if (!Supervisor.isReportable(error))
      return;
    const derror = Diagnostics.directError(error, info);
    if (derror)
    {
      if (Array.isArray(derror))
        derror.forEach((e) => this.worker.emit('error', Supervisor.coerceError(isWorkerError, e)));
      else
        this.worker.emit('error', Supervisor.coerceError(isWorkerError, derror));
    }
  }

  /**
   * Emit NON-FATAL error as directly as possible to the worker error event.
   *
   * When (error instanceof AggregateError), recursively process each element of error.errors.
   * else when error is Error-like, transform into a DCPError before emitting the worker 'error' event.
   * else when (error instanceof Error), pass directly to the worker 'error' event emitted.
   * @param {{ message: string }|string} error
   * @param {string}             [info] - addition information
   */
  directError (error, info)
  {
    return this.#directError (error, info, /*isWorkerError*/ true);
  }

  /**
   * Emit FATAL error as directly as possible to the worker error event.
   *
   * When (error instanceof AggregateError), recursively process each element of error.errors.
   * else when error is Error-like, transform into a DCPError before emitting the worker 'error' event.
   * else when (error instanceof Error), pass directly to the worker 'error' event emitted.
   * @param {{ message: string }|string} error
   * @param {string}             [info] - addition information
   */
  directFatalError (error, info)
  {
    return this.#directError (error, info, /*isWorkerError*/ false);
  }

  /**
   * Cuurrently UNUSED
   * Emit error directly to the job client using postWorkEmit.
   * @param {Slice}               slice
   * @param {{ message: string }} error - Error or Error-like
   * @param {string}             [info] - the name of the job
   */
  directJobErrorOnly (slice, error, info)
  {
    if (!Diagnostics.isDoNotEmitError(error))
      this.postWorkEmit(slice, 'error', error, info);
  }

  /**
   * Emit NON-FATAL error directly to the worker error event and postWorkEmit to job client.
   * @param {Slice}               slice
   * @param {{ message: string }} error - Error or Error-like
   * @param {string}             [info] - some additional context info
   */
   directJobError (slice, error, info)
   {
     if (Diagnostics.isDoNotEmitError(error))
       return; // To prevent rethrow and emit again.
     // Tell the job client all about it.
     this.postWorkEmit(slice, 'error', error, info);
     // Display a minimal error on the worker.
     this.directError(error, info);
   }

   /**
   * Emit NON-FATAL error directly to the worker error event and postWorkEmit to job client.
   * @param {Slice}               slice
   * @param {{ message: string }} error - Error or Error-like
   * @param {string}             [info] - some additional context info
   */
   directFatalJobError (slice, error, info)
   {
     if (Diagnostics.isDoNotEmitError(error))
       return; // To prevent rethrow and emit again.
     // Tell the job client all about it.
     this.postWorkEmit(slice, 'error', error, info);
     // Display a minimal error on the worker.
     this.directFatalError(error, info);
   }

  /**
   * Warning feedback to user.  Suppresses identical warning messages which happen more often
   * than warningTimeout. Never sends the same message twice in a row.
   * @param {string[]} messages
   */
  warning (...messages)
  {
    const warningTimeoutMs = 1e3 * (this.worker.config.warningTimeout ?? 180);
    const now = Date.now();

    debugging('supervisor') && console.debug('Supervisor.warning:', messages);
    if (messages.length < 1)
      messages = [ 'Supervisor.warning called w/o valid message(s)' ];

    this.recentWarnings.forEach((lastNow, message) => {
      if (now - lastNow > warningTimeoutMs)
        this.recentWarnings.delete(message);
    });

    messages.forEach((message) => {
      if (this.recentWarnings.has(message))
      {
        if (this.recentWarnings.last === message)
          this.recentWarnings.set(message, now); /* extend deletion time */
        return;
      }

      this.worker.emit('warning', message);
      this.recentWarnings.set(message, now);
      this.recentWarnings.last = message;
    });
  }

  /**
   * Handle notifications from the task-distributor
   * @param peerAddress - peer address for connection to scheduler (used to validate signatures)
   */
  async handleNotifications (peerAddress)
  {
    const db = await management.jnuConnect();
    const notifications = await db.select({
      table: 'w$workerNotifications',
      prototype: { workerOpaqueId: this.workerId }
    });
    const notificationIds = [];
    for (let notification = await notifications.getFirst(); notification; notification = await notifications.getNext())
    {
      notificationIds.push(notification.id);
      /** @type {*} */ // Make jsDoc shutup
      const signer = new Address(recoverAddressfromSignature(notification.event, notification.signature));
      // There are 3 valid entities for creating notifications for the worker:
      //  - the scheduler (address found via response.peerAddress)
      //  - the current identity (address found via identity.get())
      //  - the owner of this worker (address found via row.owner - the worker's identity proxy when selecting for notifications)
      if (false ||
          signer.eq(peerAddress) ||
          signer.eq(identity.get().address) ||
          signer.eq(notification.owner))
      {
        let resolveNotification;
        const notificationHandled$p = new Promise((resolve, _reject) => { resolveNotification = resolve });
        this.worker.emit('notification', notification.event, () => resolveNotification());
        await notificationHandled$p;
      }
      else
      {
        // Is this right?
        // Maybe we shouldn't kill the worker until cleanup, or another action, is complete...
        // E.g. It may be possible for DCP-WORKER to shut things down before the below deleteMany.
        // A future worker could be even more aggressive.
        this.emitFatalError(`Received notification with invalid signature: ${JSON.stringify(notification.event)}`);
      }
    }
    await db.deleteMany({
      table: 'w$workerNotifications',
      prototype: { id: { in: notificationIds }, workerOpaqueId: this.workerId },
    });
  }

  /**
   * Dump the status of all slices.
   * @param {*} [tag]
   */
  showSlices (tag = '')
  {
    for (const jm of this.jobManagerInventory)
    {
      console.log(`showSlices:${tag}: estimation=${jm.isEstimation}, workerId=${this.workerId}, jobMan=${jm.identifier}, slices=${jm.sliceInventory.map((x) => x.sliceNumber)}`);
      for (const slice of jm.sliceInventory)
        console.log(`\t${slice.identifier}`);
    }
  }

  /**
   * Transition from state 'ready to 'paused'.
   * When paused, the worker will not execute new slices or fetch new work.
   * But it will be alive and well, waiting to be unpaused.
   * No slices will be returned to scheduler.
   *
   * NOTE - pause() is not a DistributiveWorker API; it exists here to support Soteria testing.
   */
  pause ()
  {
    selectiveSupEx() && console.debug(`Supervisor.pause: workerId ${this.workerId}`);
    if (this.state.isNot('paused'))
      this.state.set('ready', 'paused');
    //this.showSlices('pause');
  }
  /**
   * Transition to state 'paused' to 'ready'.
   * The worker will now execute new slices and fetch new work.
   */
  unpause ()
  {
    selectiveSupEx() && console.debug(`Supervisor.unpause: workerId ${this.workerId}`);
    if (this.state.isNot('ready'))
      this.state.set('paused', 'ready');
  }

  // _Idx
  //
  // destroyAllJobs, stopAndShutdown
  // stopWork, purgeJob, rejectJob
  //

  /**
   * Terminate sandboxes down to current target cpu/gpu core count * utilization
   */
  async dropSandboxes()
  {
    const orderedSandboxes = this.workingSandboxes.sort((a,b) => b.sliceStartTime - a.sliceStartTime);
    var targetCPU = this.worker.config.cores.cpu * this.worker.config.utilization.cpu;
    var targetGPU = this.worker.config.cores.gpu * this.worker.config.utilization.gpu;
    let workingCPUDensity = this.workingSliceDensity;
    let workingGPUDensity = this.workingGPUDensity;
    const terminatedSlices = [];

    if (targetCPU < 0)
    {
      this.warning(`${targetCPU} CPU consumption target rounded to 0`);
      targetCPU = 0;
    }
    if (targetGPU < 0)
    {
      this.warning(`${targetGPU} GPU consumption target rounded to 0`);
      targetGPU = 0;
    }

    while (orderedSandboxes.length && (workingCPUDensity > targetCPU || workingGPUDensity > targetGPU))
    {
      const sandbox = orderedSandboxes.shift();
      if (workingCPUDensity <= targetCPU && !sandbox.jobManager.hasGPU)
        continue;

      sandbox.terminate(true);
      assert(this.sandboxInventory.indexOf(sandbox) === -1);
      terminatedSlices.push(sandbox.slice);
      workingCPUDensity -= sandbox.jobManager.estimateCPUDensity;
      workingGPUDensity -= sandbox.jobManager.estimateGPUDensity;
    }
    await this.returnSlices(terminatedSlices, 'Worker wanted fewer running slices');
  }

  /**
   * Call when shutting down in stopWork and evaluatorEnd
   */
  async destroyAllJobs (evaluatorDown = false)
  {
    // First terminate all sandboxes.
    // If any non-terminated sandboxes exist, a localexec job may not exit.
    while (this.sandboxInventory.length)
      this.returnSandbox(this.sandboxInventory[0], true);
    selectiveDebug() && console.debug(`Supervisor.destroyAllJobs(${this.state},${evaluatorDown}): terminated all sandboxes`, this.sandboxInventory.map((s) => s.identifier));
    this.sandboxInventory = [];
    // Clean up anything left-over.
    for (const jm of this.jobManagerInventory)
      jm.destroyHelper();
    if (!evaluatorDown)
    {
      // Reset job dependent structures.
      this.jobManagerInventory = common.InventoryArray('jobManagers');
      this.moduleCache = new ModuleCache(this);
      this.unresolvedJobs = [];
    }
    this.cursor = null;
    // Send message to scheduler that returns all slices owned by this worker.
    return this.sendReturnAllSlicesMessage();
  }

  /**
   * Use explicit awaits to make sure things get shutdown properly.
   * Probably being overly conservative, but there have been problems
   * shutting down before the function exits.
   * @returns {Promise<*>}
   */
  async stopAndShutdown ()
  {
    try
    {
      await this.destroyAllJobs();
    }
    finally
    {
      try
      {
        // Shutdown TD, RS, ER, PM
        await this.dcp4.closeConnections();
      }
      catch(error) { error, 'dontcare' }
      finally
      {
        if (this.state.isNot('stopped'))
          this.state.set('stopping', 'stopped');
        // This log message assume slices were returned to scheduler in a previous operation, which is the only current use case.
        // If we use this function in a different way in the future, update the log message.
        selectiveSupEx() && console.debug(`Supervisor.stopAndShutdown(${this.state}): terminated all sandboxes and returned all slices to scheduler...`, this.sandboxInventory.map((s) => s.identifier));
      }
    }
  }

  /**
   * Terminates sandboxes and returns slices.
   * Sets the working flag to false, call this.work() to start working again.
   *
   * If forceTerminate is true: Terminates all sandboxes and returns all slices.
   * If forceTerminate is false: Terminates non-working sandboxes and returns initial and ready slices.
   *
   * @param {boolean} [forceTerminate = true] - true if you want to stop the sandboxes from completing their current slice.
   * @returns {Promise<*>}
   */
  async stopWork (forceTerminate = true)
  {
    /** @returns {boolean} */
    const doNotWaitForWork = () => {
      return (this.evaluator.reallyDown || this.sandboxInventory.filter(sbx => !sbx.isTerminated).length < 1);
    }
    selectiveDebug() && console.debug(`Supervisor.stopWork(${forceTerminate}, ${this.state}): terminating sandboxes and returning slices to scheduler.`);

    // [ 'initial', 'ready', 'stopping', 'stopped', 'paused', 'broken']
    if (this.state.is('stopped') || !forceTerminate && this.state.is('stopping'))
    {
      this.warning(`Supervisor.stopWork was called when supervisor is in state ${this.state.valueOf()}.`, 'Please either wait and try again or restart worker.');
      return;
    }

    try
    {
      // Set state to 'stopping'.
      if (this.state.isNot('stopping'))
        this.state.set(['ready', 'paused', 'broken', 'initial'], 'stopping');

      // Clear the timers.
      if (this.watchdogTimer)
        dcp_timers.clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
      if (this.suspensionDetectionInterval)
        dcp_timers.clearTimeout(this.suspensionDetectionInterval);
      this.suspensionDetectionInterval = null;
      if (this.detectedTabTimer)
        dcp_timers.clearTimeout(this.detectedTabTimer);
      this.detectedTabTimer = null;
      if (this.sliceDebuggingTimer)
        dcp_timers.clearInterval(this.sliceDebuggingTimer);
      this.sliceDebuggingTimer = null;

      if (!forceTerminate)
      {
        for (let k = 0; k < 3; k++)
        {
          await new Promise((resolve) => {
            // Count the slices that have been working or close-to-working but haven't submitted results yet.
            let activeSliceCount = 0;
            for (const jm of this.jobManagerInventory)
              activeSliceCount += jm.activeSlices.length;
            // When no active slices we're done.
            if (activeSliceCount === 0)
              resolve();
            // When no work can be completed we return all slices and leave.
            if (doNotWaitForWork())
              resolve();
            selectiveDebug() && console.debug(`StopWork: waiting for ${activeSliceCount} working slices to finish`, k);
            // Resolve and finish stopWork once all sandboxes have finished submitting their results.
            this.worker.on('result', (...ev) => {
              selectiveSupEx() && console.debug(`StopWork(${this.workerId}): result event handler, activeSliceCount ${activeSliceCount}`, utils.stringify(ev));
              if (--activeSliceCount === 0)
                resolve();
            });
            // When sandox.work fails we also decrement activeSliceCount.
            this.on('sliceFailed', () => {
              selectiveSupEx() && console.debug(`StopWork(${this.workerId}): sliceFailed event handler, activeSliceCount ${activeSliceCount}`);
              if (--activeSliceCount === 0)
                resolve();
            });
            // Skip waiting when evaluator down.
            this.on('evalDown', () => {
              this.warning('Evaluator cannot create any sandbox; returned all slices to scheduler.');
              resolve();
            });
          });
        }

        if (selectiveDebug())
        {
          console.debug(`stopWork(${this.state.valueOf()}): After waiting for working slices to finish: workingSbxes: ${this.workingSandboxCount}, totalSbxes: ${this.sandboxInventory.length}, jobs: ${this.jobManagerInventory.length}`);
          this.jobManagerInventory.forEach((jm) => {
            console.debug('stopWork job', jm.identifier);
            console.debug(jm.countSliceStr('stopWork'));
          });
        }
      }
    }
    finally
    {
      selectiveDebug() && this.warning('All sandboxes empty, stopping worker and closing all connections.');
      return this.stopAndShutdown();
    }
  }

  /**
   * Purge all traces of the job.
   * @param {JobManager}   jobManager
   * @param {string|{errorCode: string, message: string|false}} reason
   */
  async purgeJob (jobManager, reason)
  {
    selectiveSupEx() && console.debug(`Supervisor.purgeJob ${jobManager.identifier}`, this.unresolvedJobs);
    if (!jobManager.dead)
    {
      // jobManager will be either in this.jobManagerInventory or this.unresolvedJobs.
      if (!common.removeElement(this.unresolvedJobs, jobManager.jobId))
        this.jobManagerInventory.delete(jobManager);
      else
        assert(this.jobManagerInventory.filter((jm) => jm.jobId === jobManager.jobId).length < 1);
      this.moduleCache.removeJob(jobManager.jobId);
      // DANGER: Do not call JobManager.destroy(true) anywhere else.
      await jobManager.destroy(true /*itsDeadJim*/, reason);
    }
  }

  /**
   * Purge all traces of the job and prevent slices from being fetched from Task Distributor.
   * @param {JobManager}   jobManager
   * @param {string|{errorCode: string, message: string|false}} reason
   */
  rejectJob (jobManager, reason)
  {
    this.purgeJob(jobManager, reason);
    this.rejectedJobs.push(jobManager.jobId);
  }

  // _Idx
  //
  // roundRobinSlices, makeJobSelectionCursor, handleSandboxWorkError, handleFailedSlice
  //

  /**
   * Note: It's not currently necessary to wrap _roundRobinSlices in static and dynamic catch blocks.
   *       But in the future, changes happen and bugs exist.
   *
   * Round-robin through the job managers, picking 1 slice to run each time.
   * Try to have the same number of working sandboxes for each job.
   * Try to run a slice on every available sandbox.
   *
   * The basic idea behind the scheduling of slices in this implementation is to keep as
   * many slices from different jobs running as possible, so as to reduce the likelihood
   * of resource contention between sandboxes.
   *
   * Slices are scheduled based on the following ruleset:
   * 1) Order the jobs as described below and pick 1 slice from each job in a round-robin manner,
   *    so the order of the jobs is the determining factor for the slices that are chosen.
   *    Thus in what follows we'll only talk about job order.
   * 2) cursor = makeJobSelectionCursor(), then cursor.next() returns a slice chosen as follows.
   * 3) Let concurrency range from 1 to maxWorkingCores.
   * 4) For a given concurrency, let readyJobs be all jobs such that jobMan.workingSliceDensity < concurrency.
   * 5) Do an ascending sort of readyJobs wrt jobMan.emaElapsedTime.
   * 6) Pick the longest job in readyJobs that doesn't have any executing slices.
   * 7) Alternately shift a job from readyJobs vs choose a job from a random nearly finished job, and remove job from readyJobs.
   * 8) When there are no more almost finished jobs with slices, shift jobs from readyJobs.
   * 9) Jobs which have slicePriority set by the task-distributor may have slices chosen ahead of the above algorithm.
   * 10) Jobs with a slicePriority closer to 1 are more likely to be chosen.
   * 11) After finishing concurrency at maxWorkingCores, cursor.next() returns null, so create a new cursor.
   * @returns {Promise<any>}
   */
  async roundRobinSlices ()
  {
    if (!this.isReadyForWork)
      return;

    try
    {
      this.#roundRobinSlices();
    }
    catch (error)
    {
      this.directFatalError(error, 'Failed to run slices.');
    }
  }

  /**
   * Run slice on sandbox, with error handling.
   * @param {Slice} slice
   * @param {Sandbox} sandbox
   */
  async #runSliceOnSandbox (slice, sandbox)
  {
    return slice.jobManager.runSliceOnSandbox(slice, sandbox).catch((error) => {
      selectiveSupEx() && console.log(`#runSliceOnSandbox(${sandbox.identifier},${sandbox.endEventFired}):`, slice.identifier, this.evaluator, error);
      // We should never get here, except when sandbox has been terminated by intentionally reducing available sandbox count (e.g. screensaver down or evaluator sandbox throttling...)
      // Or a transient network error.
      this.returnSlice(slice, common.errorCodeOrString(error, `Failed to run slice ${slice.identifier} on sandbox ${sandbox.identifier}`));
      this.returnSandbox(sandbox);
      if (sandbox.endEventFired)
        selectiveDebug() && this.directError(error, `Failed to run slice ${slice.identifier} on sandbox ${sandbox.id}; Evaluator sandbox for worker ${this.workerId} may be down.`);
      else
      {
        // Not sure that a fatal error is appropriate here.
        // (pfr) We could end up here because recordResult failed unexpectedly.
        /** @todo Make the recordResult path more robust. */
        this.directFatalError(error, `Failed to run slice ${slice.identifier} on worker ${this.workerId}.`);
      }
    });
  }

  /**
   * Find an assigned sandbox on which to run slice.
   * @param {Slice} slice
   * @returns {Promise<*>}
   */
  async #findSandboxAndRunSlice (slice)
  {
    selectiveDebug() && console.debug('findSandboxAndRunSlice:', slice.identifier, this.sandboxInventory[0]?.isReady, slice.jobManager.assignedSandboxes.length);
    try
    {
      assert(this.cursor && slice?.isReserved);

      /** @type {Sandbox} */
      let sandbox;
      if (this.sandboxInventory[0]?.isReady)
        sandbox = this.sandboxInventory.shift();
      else
        sandbox = await this.sandboxBackoffManager.create();

      const job = slice.jobManager;
      if (!sandbox) // Cannot create a sandbox.
      {
        if (job.assignedSandboxes.length > 0) // Check again for an assigned sandbox.
          sandbox = job.assignedSandboxes[0].markAsReserved();
        else
        {
          // Undo GPU state.
          slice.clearGPUState();
          // Revert slice to READY so it can be picked up again for scheduling.
          slice.resetFromReserved();

          if (common.highPriorityDiag(/*displayOnDebugBuild*/ false))
            return this.warning(`Cannot create new sandbox for slice ${slice.identifier}...will retry.`);
          return this.warning('Sandbox creation timeout (busy evaluator?)');
        }
      }

      if (sandbox.isReady)
      {
        // Assign sandbox.
        // If failure, then allow same slice to try for an assigned sandbox one more time.
        // Otherwise return slice to scheduler and save the READY sandbox.
        // The reason for the retry is sometimes sandbox.assign will only succeed for a given
        // job when not done simultaneously with other sandboxes.
        try
        {
          await sandbox.assign(slice);
        }
        catch (error)
        {
          if (typeof error === 'string')
            error = {
              message: error,
              job: job.jobId,
              jobName: job.public.name,
              timestamp: new Date(),
            };

          // Sandbox failed to get job artifacts, could be unrecoverable so kill it.
          sandbox.terminate(false);
          // Undo GPU state.
          slice.clearGPUState();

          // 1st check whether evaluatorHandle 'end' event fired -- it might be throttling of some
          // form so reuse the slice. Also reuse slice is a transient network error occured.
          // However, if the evaluator is down then the worker is put in a wakeable, quiescent state
          // and we tell TD to go through the activeSlices table and marked the slices owned by the
          // current worker as returned.
          if (sandbox.endEventFired || !Supervisor.isReportable(error))
            // Revert slice to READY so it can be picked up again for scheduling.
            return slice.resetFromReserved();
          else
          {
            this.directJobError(slice, error, `Failed to assign job ${job.jobId} to sandbox`);
            if (slice.jobManager.consecutiveSandboxAssignFailures >= this.options.consecutiveAssignFailureLimit)
              return this.purgeJob(slice.jobManager, `sandbox.assign for job ${job.jobId} failed too often; purging job from worker ${this.workerId}`);
            // Should we try to reuse the slice once?
            return this.returnSlice(slice, common.errorCodeOrString(error, `Failed to assign job ${job.jobId} to sandbox`));
          }
          // @ts-ignore
          assert(!"UNREACHED");
        }
        // Mark as reserved and push to inventory.
        sandbox.markAsReserved();
        this.sandboxInventory.push(sandbox);
      }
      return this.#runSliceOnSandbox(slice, sandbox);
    }
    catch (error)
    {
      // Paranoia -- should never get here.
      this.returnSlice(slice, common.errorCodeOrString(error, `Failed to create sandbox or run slice ${slice.identifier}`));
      this.directFatalError(error, `Failed to create sandbox or run slice ${slice.identifier}`);
    }
  }

  /**
   * Calculate how long to pause until scheduling the next GPU slice.
   * Suppose a GPU slice has GPU density 1, but cores.gpu is 0.75 then after executing the slice
   * we pause further GPU scheduling for timespan
   *     slice.gpuOverage * GPUTime := ((1.0 / 0.75) - 1.0) * GPUTime = (1/3) * GPUTime
   * @param {Slice}  slice
   * @param {number} GPUTime
   */
  quantizeGPUExecution (slice, GPUTime)
  {
    if (slice.gpuOverage)
      this.pauseGPUSchedulingUntil = Date.now() + slice.gpuOverage * GPUTime;
  }

  /**
   * Private, do not use; call this.roundRobinSlices() instead.
   *
   * Round-robin through the job managers, picking 1 slice to run each time.
   * Try to have the same number of working sandboxes for each job.
   * Try to run a slice on every available sandbox.
   * @returns {Promise<any>}
   */
  async #roundRobinSlices ()
  {
    // Debugging data to trace slice scheduling logic.
    const rrs = {
      barrier: this.roundRobinBarrier,     // early return
      evalDown: this.evaluator.reallyDown, // early return
      cpuSpace: 0,
      gpuSpace: 0,
      sandboxSpace: 0,
      emptyCursor: false,
      loops: [],
    };
    this.rrsHistory.rb.push(rrs);

    //
    // Should we try to put all runSlice promises in an array and return Promise.all(runslice-promises) ?
    //
    try
    {
      selectiveDebug2() && console.debug('BarrierState:RRS:', this.fetchTaskBarrier, this.roundRobinBarrier, this.evaluator);
      // roundRobinBarrier is a barrier for the slice execution path.
      // When eval is down, don't execute slices.
      if (this.roundRobinBarrier || !this.isReadyForWork)
        return;
      this.roundRobinBarrier = true;

      // The amount of unusedCoreSpace available for new slices to run in sandboxes.
      // If unusedCoreSpace is 2.5 and there are 6 slices with density 0.4, and there are enough non-working usable
      // sandboxes, then all 6 slices will be scheduled to run.
      rrs.cpuSpace = this.unusedCoreSpace;
      // The number of sandboxes not currently being used.
      rrs.sandboxSpace = this.unusedSandboxCount;
      // Sum of GPU densities of all working GPU slices.
      const workingGPUDensity = this.workingGPUDensity;
      // Unused space of GPUs + extra space allowed for GPU quanta rounding.
      rrs.gpuSpace = this.usableGPUCount - workingGPUDensity;

      // Tighten schedulingSlop when there's only 1 cpu core.
      const schedulingSlop = (this.maxWorkingCores > 1) ? common.schedulingSlop : 0.5 * common.schedulingSlop;

      // Early exit when not enough space.
      if (rrs.cpuSpace < schedulingSlop || rrs.sandboxSpace < 1)
      {
        selectiveDebug2() && console.debug('RRS: bail early space/barrier/unusedSlots', rrs.cpuSpace, this.roundRobinBarrier, rrs.sandboxSpace);
        return;
      }

      if (false || selectiveDebug())
      {
        let totalReady = 0, totalReadyDensity = 0;
        for (const jobMan of this.jobManagerInventory)
        {
          const currentReady = jobMan.readySlices.length, currentReadyDensity = currentReady * jobMan.estimateCPUDensity;
          totalReady += currentReady;
          totalReadyDensity += currentReadyDensity;
          console.debug(`RRS: job ${jobMan.identifier}, density ${jobMan.estimateCPUDensity}, readySlices ${currentReady}, readyDensity ${currentReadyDensity}`);
        }
        console.debug(`RRS: space ${rrs.cpuSpace}, unusedSandboxCount ${rrs.sandboxSpace}, totalReady ${totalReady}, totalReadyDensity ${totalReadyDensity}`);
        if (false || selectiveDebug2())
        {
          this.jobManagerInventory.forEach((jobMan) => {
            const { unassigned, ready, reserved, working, workdone, complete, failed, finished } = jobMan.dumpSlices ('RRS', false, false);
            console.debug(`RRS(${jobMan.identifier},${rrs.sandboxSpace},${rrs.cpuSpace}): u/r/rsv/w/wd/c/f/fsh ${unassigned}/${ready}/${reserved}/${working}/${workdone}/${complete}/${failed}/${finished}`, jobMan.identifier, this.sandboxInventory.length);
          });
        }
      }

      /** @type {Slice[]} */
      const slices = [];
      /** @type {number} */
      let cpuDensity = 0;
      /** @type {number} */
      let gpuDensity = 0;

      const isSpaceAvailable = () => {
        selectiveDebug2() && console.debug('RRS: isSpaceAvailable', cpuDensity < rrs.cpuSpace - schedulingSlop, slices.length < rrs.sandboxSpace);
        return cpuDensity < rrs.cpuSpace - schedulingSlop && slices.length < rrs.sandboxSpace;
      }

      // When there's no more CPU space, break out of the loop.
      // When there's no more GPU space and there is CPU space, tell the cursor to not pick
      // any more slices from GPU jobs, but continue trying to find slices from CPU jobs.

      // Get existing cursor or create new one.
      if (!this.cursor)
        this.cursor = this.makeJobSelectionCursor();
      this.cursor.useGPU(true); // Restore use of GPUs.

      //
      // DANGER: Do not await any promises in between:
      //     const slice = this.cursor.next()
      //   and
      //     this.cursor.commit(slice) or this.cursor.commit()
      //

      //
      // Think about keeping track of GPU jobs and CPU jobs in this.jobManagerInventory that still
      // have queued slices. I think 2 measures are sufficient:
      //    gpuSlicesExist
      //    cpuSlicesExist
      // Collect 2 arrays of CPU and GPU jobs after clearUnusedJobs.
      // When a job runs out of slices, we mark the job as having no queued slices (empty) and
      // remove it from the respective array. So all we need to check is whether each of these
      // 2 arrays is non-empty.
      //   With such information we'll be able to make more intelligent decisions, but need to be
      // careful that the extra complexity (and possible fragility) is worth it.
      //
      selectiveDebug() && console.debug(`RRS: sandboxSpace ${rrs.sandboxSpace}, cpuSpace ${rrs.cpuSpace}, gpuSpace ${rrs.gpuSpace}`);

      let createdNewCursor = false; // Only allow creating a new cursor once.
      while (isSpaceAvailable())
      {
        // Get the next slice, then check to see whether it can be used.
        // The cursor may be at the end, so reset to the beginning before giving up.
        let slice = this.cursor.next();
        if (!slice) // Try resetting the cursor once.
        {
          if (createdNewCursor)
            break;
          this.cursor = this.makeJobSelectionCursor();
          slice = this.cursor.next();
          if (!slice) // No slices available
            break;
          createdNewCursor = true;
        }

        const job = slice.jobManager;
        // Loop statistics.
        const loop = {
          time: new Date(),
          cpuDensity,
          gpuDensity,
          sliceCnt: slices.length,
          slice: {
            sliceNumber: slice.sliceNumber,
            jobId: job.jobId,
            name: job.public?.name,
            jobCPUDensity: job.estimateCPUDensity,
            jobGPUDensity: job.estimateGPUDensity,
            queued: job.queuedSlices.length,
            hasGPU: job.hasGPU,
            commited: false,
            gcommited: false,
          },
        };
        rrs.loops.push(loop);

        // Check to see if slice fits into CPU space.
        if (cpuDensity + job.estimateCPUDensity > rrs.cpuSpace + schedulingSlop)
          break; // CPU space is full.
        // Check to see if slice fits into GPU space
        let scheduleGPU = false;
        if (job.hasGPU)
        {
          const estimateGPUDensity = job.estimateGPUDensity;
          if (Date.now() >= this.pauseGPUSchedulingUntil
            && gpuDensity + estimateGPUDensity <= rrs.gpuSpace
            && (this.concurrentGPUSlices || workingGPUDensity === 0))
          {
            gpuDensity += estimateGPUDensity;
            // GPU slice is being scheduled.
            selectiveSupEx() && console.debug(`Scheduling GPU slice ${slice.sliceNumber} of job ${slice.jobId}: current scheduled GPU density ${gpuDensity} <= available GPU density ${rrs.gpuSpace}`);
            scheduleGPU = true;
            if (gpuDensity + workingGPUDensity > this.maxWorkingGPUs)
              slice.gpuOverage = ( (gpuDensity + workingGPUDensity) / this.maxWorkingGPUs - 1.0 );
            if (!this.concurrentGPUSlices)
              this.cursor.useGPU(false); // Stop looking for GPU work until next RRS call.
          }
          else
          {
            // Advance cursor and don't try to fetch slices from GPU jobs until the next RRS call.
            /** XXXpfr @todo Weakness: Another slice could have smaller job.estimateGPUDensity which could fit. */
            // This weakness will be a problem when webGPU supports multiple concurrent kernels
            // on the same GPU, like CUDA does.
            this.cursor.commit(); // Skip current slice.
            this.cursor.useGPU(false); // Stop looking for GPU work until next RRS call.
            continue;
          }
        }
        this.cursor.commit(slice); // Advance cursor and reserve slice.
        cpuDensity += job.estimateCPUDensity;
        slices.push(slice);

        // Record commit in loop history.
        loop.sliceCnt++;
        loop.slice.commited = true;
        loop.slice.gcommited = scheduleGPU;
        loop.cpuDensity = cpuDensity;
        loop.gpuDensity = gpuDensity;
        this.rrsHistory.lastSuccess = rrs;

        // Try use an existing assigned sandbox.
        // Note: Cannot await here because it could diminish parallelism of sandboxes.
        if (job.assignedSandboxes.length < 1)
          this.#findSandboxAndRunSlice(slice);
        else
        {
          const sandbox = job.assignedSandboxes[0].markAsReserved();
          this.#runSliceOnSandbox(slice, sandbox);
        }

        if (selectiveDebug2())
        {
          job.hasGPU && console.debug(`RRS: GPU scheduling: gpuDensity/gpuSpace ${gpuDensity.toFixed(7)}/${rrs.gpuSpace.toFixed(7)}, jobGPUDensity/jobCPUDensity ${job.estimateGPUDensity.toFixed(7)}/${job.estimateCPUDensity.toFixed(7)}`);
          console.debug('RRS: density/space/numSlices/unusedSlots/jobDensity/workingSlices', cpuDensity, rrs.cpuSpace, slices.length, rrs.sandboxSpace, job.estimateCPUDensity, this.workingSliceCount);
        }
      }
      selectiveDebug() && cpuDensity > 0 && console.debug(`roundRobinSlices(${this.workingSliceCount},${this.workingSliceDensity},${this.workerId}): Found density ${cpuDensity.toFixed(7)}/${rrs.cpuSpace} with ${slices.length} slices:`, slices.map((slice) => slice.identifier), this.jobManagerInventory.map((jm) => `${jm.identifier}:${jm.estimateCPUDensity.toFixed(7)}:${jm.emaCPUTime.toFixed(0)}`));
    }
    finally
    {
      this.roundRobinBarrier = false;
    }
  }

  /**
   * @private
   * @callback cbNext
   * @returns {Slice}
   */
  /**
   * @private
   * @callback cbCommit
   * @param {Slice} [slice]
   */
  /**
   * @private
   * @callback cbUseGPU
   * @param {boolean} b
   */

  /**
   * Factory function which instantiates a JobSelectionCursor.  A JobSelectionCursor
   * steps the order that job slices should be selected for execution in the supervisor,
   * given the current state of the supervisor and the availability of jobs when the
   * inventory was snapshot.  The entire slice scheduling algorithm is represented by
   * this cursor.
   *
   * The basic idea behind the scheduling of slices in this implementation is to keep as
   * many slices from different jobs running as possible, so as to reduce the likelihood
   * of resource contention between sandboxes.
   *
   * Slices are scheduled based on the following ruleset:
   * 1) Order the jobs as described below and pick 1 slice from each job in a round-robin manner,
   *    so the order of the jobs is the determining factor for the slices that are chosen.
   *    Thus in what follows we'll only talk about job order.
   * 2) cursor = makeJobSelectionCursor(), then cursor.next() returns a slice chosen as follows.
   * 3) Let concurrency range from 1 to maxWorkingCores.
   * 4) For a given concurrency, let readyJobs be all jobs such that jobMan.workingSliceDensity < concurrency.
   * 5) Do an ascending sort of readyJobs wrt jobMan.emaElapsedTime.
   * 6) Pick the longest job in readyJobs that doesn't have any executing slices.
   * 7) Alternately shift a job from readyJobs vs choose a job from a random nearly finished job, and remove job from readyJobs.
   * 8) When there are no more almost finished jobs with slices, shift jobs from readyJobs.
   * 9) Jobs which have slicePriority set by the task-distributor may have slices chosen ahead of the above algorithm.
   * 10) Jobs with a slicePriority closer to 1 are more likely to be chosen.
   * 11) After finishing concurrency at maxWorkingCores, cursor.next() returns null, so create a new cursor.
   *
   * A custom selection of jobs can be passed in via the argument jobManagers.
   *
   * @param {JobManager[]} [jobManagers]
   * @returns {{ next: cbNext, commit: cbCommit, useGPU: cbUseGPU }}
   */
  makeJobSelectionCursor (jobManagers)
  {
    /* Variables in this scope function as state information for next() */
    /** @type {JobManager[]} */
    var candidateJobs;       // The jobs available with slices ready to execute.
    /** @type {JobManager[]} */
    var readyJobs;           // The jobs from which slices are selected for a given concurrency level.
    /** @type {JobManager[]} */
    var preferedJobs = [];   // Those jobs in readyJobs with a slicePreference property.
    /** @type {JobManager[]} */
    var lowDensityJobs = []; // Jobs with density <= 0.6, will be scheduled again.
    /**
     * Upper bound of the sum of the working slices densities allowed for a given job.
     * type {number}
     **/
    var concurrency = 0;
    /** type {number} */
    var jobIdx = 0;
    /** @type {boolean} */
    var lowDensityPass = false;
    /** @type {number} */
    var longSlicesAvailableToSchedule = Number.MAX_SAFE_INTEGER;
    /** @type {boolean} */
    var peekPrefered = false;
    /** @type {boolean} */
    var skipGPUJobs = false;

    const that = this;
    if (!jobManagers)
      jobManagers = this.jobManagerInventory;

      /** @param {JobManager[]} jobs */
    const jobStateStr = (jobs) => {
      return jobs.map((jm) => `${jm.identifier} : ${jm.readySlices.length} : ${jm.workingSliceDensity} : ${Math.round(jm.emaElapsedTime)}`);
    }
    const jobState = (hdr, jobs) => { console.debug(hdr, jobStateStr(jobs)); }

    /**
     * Do not allow excessive working long slices to block short slices from completing in a
     * reasonable amount of time. We don't want slices from all long jobs to consume more than
     * X% of cores.cpu, when there still exist short slices, This function limits the # of long
     * slices that can be scheduled by limiting longSlicesAvailableToSchedule.
     * Note: There can be no more than cores.cpu long slices in a worker.
     */
    function preventLongSlicesFromHoggingCompute ()
    {
      let workingLongSliceCount = 0, shortSlicesExist = false, longSlicesExist = false;
      for (const jm of jobManagers)
        if (jm.queuedSlices.length > 0)
        {
          if (!jm.isLong)
            shortSlicesExist = true;
          else
          {
            longSlicesExist = true;
            workingLongSliceCount += jm.workingSliceCount;
          }
        }
        // When both long and short slices exist and that.maxWorkingCores === 1, then longSlicesAvailableToSchedule === 0.
        // Effectively this says to schedule short slices first.
        longSlicesAvailableToSchedule = (shortSlicesExist && longSlicesExist)
        ? Math.max(0, Math.floor(that.options.maxProportionOfLongJobs * that.maxWorkingCores) - workingLongSliceCount)
        : Number.MAX_SAFE_INTEGER
    }

    /**
     * Populate readyJobs with jobs which are ready and have at least one slice which is ready,
     * and whose # of working slice density is less than concurrency. A reserved slice has a
     * finite lifetime and if exceeded, transition it back to ready.
     * @param {JobManager[]} jobManagers
     * @param {number}       concurrency
     */
    function filterJobsAndCheckOldReservedSlices (jobManagers, concurrency) // eslint-disable-line no-shadow
    {
      candidateJobs = [], readyJobs = [];
      for (const jobMan of jobManagers)
      {
        if (!jobMan.ready || skipGPUJobs && jobMan.hasGPU || jobMan.isLong && longSlicesAvailableToSchedule < 1)
          continue;
        let readyCount = 0;
        for (const slice of jobMan.sliceInventory)
        {
          if (slice.isReady)
            readyCount++;
        }
        if (readyCount > 0)
        {
          candidateJobs.push(jobMan);
          if (jobMan.workingSliceDensity < concurrency)
            readyJobs.push(jobMan);
        }
      }
    }

    function seed (concurrency) // eslint-disable-line no-shadow
    {
      /* Reset. */
      jobIdx = 0;

      /* Populate readyJobs with jobs which are ready and have at least one slice which is ready,
          and whose # of working slice density is less than concurrency. */
      filterJobsAndCheckOldReservedSlices(jobManagers, concurrency);
      /* Do not allow excessive working long slices to block short slices from completing in a
         reasonable amount of time. */
      preventLongSlicesFromHoggingCompute();
      // candidateJobs = jobManagers.filter((jobMan) => jobMan.readySlices.length > 0);
      // readyJobs = candidateJobs.filter((jobMan) => jobMan.workingSliceDensity < concurrency);

      if (!lowDensityPass && lowDensityJobs.length === 0)
        lowDensityJobs = jobManagers.filter((jm) => jm.estimateCPUDensity > 0 && jm.estimateCPUDensity <= 0.6);

      if (readyJobs.length > 1)
      {
        /* Asc sort by shortest average slice completion time. */
        const shortestSliceJobs = readyJobs.sort((a, b) => Math.round(a.emaElapsedTime) - Math.round(b.emaElapsedTime));
        const almostDoneIndices = shortestSliceJobs.filter((jm) => jm.almostDone).map((_, idx) => idx);
        readyJobs = [];

        /* Find longest job that isn't working. */
        for (let k = shortestSliceJobs.length - 1; k >= 0; k--)
        {
          const jobMan = shortestSliceJobs[k];
          if (jobMan.isNotWorking)
          {
            readyJobs.push(jobMan);
            shortestSliceJobs.splice(k, 1);
            break;
          }
        }

        /* Alternate the next shortest slice with a random almost done job. */
        if (almostDoneIndices.length > 0)
        {
          while (shortestSliceJobs.length > 0)
          {
            readyJobs.push(shortestSliceJobs.shift());
            if (almostDoneIndices.length < 1)
              break;
            else
            {
              const almostDoneIdx = almostDoneIndices[Math.floor(Math.random() * almostDoneIndices.length)];
              readyJobs.push(shortestSliceJobs[almostDoneIdx]);
              shortestSliceJobs.splice(almostDoneIdx, 1);
            }
          }
        }
        if (shortestSliceJobs.length > 0)
          readyJobs.push(...shortestSliceJobs);
      }
      /* Populate preferedJobs with jobs from readyJobs which also have a slicePreference set. */
      preferedJobs = candidateJobs.filter((jm) => jm.hasOwnProperty('slicePreference'));
      selectiveDebug2() && jobState(`makeJobSelectionCursor:seed(${concurrency}): readyJobs:`, readyJobs);
    }

   /**
    * Each invocation of next() identifies one slice to run, or returns false if none can run.
    * @returns {Slice}
    */
    function next ()
    {
      if (concurrency === 0)
        seed(++concurrency);

      selectiveDebug2() && console.debug(`makeJobSelectionCursor(cc/idx/ready/working):next(${concurrency},${jobIdx},${readyJobs.length},${that.workingSliceDensity}): maxWorkingCores ${that.maxWorkingCores}: begin`);
      while (true)
      {
        if (jobIdx >= readyJobs.length)
        {
          if (++concurrency > that.maxWorkingCores)
            break;
          seed(concurrency);
        }

        if (readyJobs.length < 1)
        {
          if (candidateJobs.length < 1)
            break;
          continue; /* No ready jobs at current concurrency level. */
        }

        selectiveDebug2() && console.debug(`makeJobSelectionCursor:next(${concurrency},${jobIdx},${readyJobs.length},${that.workingSliceDensity}): before loop`);

        /* Schedule a prefered job slice based on random chance. */
        if (preferedJobs.length > 0)
        {
          let prioRan = Math.random();
          let list = preferedJobs.filter((jm) => jm['slicePreference'] >= prioRan);

          if (list.length > 0)
          {
            const jobMan = list[list.length * Math.random()];
            const slice = jobMan.readySlice();
            // Need to know we have a long slice before decrementing longSlicesAvailableToSchedule.
            // If there's no more space for long slices then proceed to readyJobs.
            if (slice && (!jobMan.isLong || longSlicesAvailableToSchedule-- > 0))
            {
              peekPrefered = true;
              return slice;
            }
          }
        }

        /* Schedule a slice from next ready job. */
        while (jobIdx < readyJobs.length)
        {
          const jobMan = readyJobs[jobIdx];
          const slice = jobMan.readySlice();
          if (false || selectiveDebug2())
          {
            slice && console.debug(`makeJobSelectionCursor:next(${concurrency},${lowDensityPass},${jobIdx},${readyJobs.length},${that.workingSliceDensity}): found slice(slice:ready:working)`, `${slice.identifier} : ${slice.jobManager.readySlices.length} : ${slice.jobManager.workingSliceDensity}`);
            !slice && console.debug(`makeJobSelectionCursor:next(${concurrency},${lowDensityPass},${jobIdx},${readyJobs.length},${that.workingSliceDensity}): no slices ready for job`, jobMan.identifier);
          }
          // Need to know we have a long slice before decrementing longSlicesAvailableToSchedule.
          if (slice && (!jobMan.isLong || longSlicesAvailableToSchedule-- > 0))
            return slice;
          jobIdx++;
        }

        /*
         * We did not schedule a slice with current seed. We need to re-seed to look for newly-available work
         * and sandboxes, ratcheting up the concurrency (max # of each job running) until we find something.
         */
        selectiveDebug2() && console.debug(`makeJobSelectionCursor:next(${concurrency},${jobIdx},${readyJobs.length},${that.workingSliceDensity}): after loop`);
      }
      if (!lowDensityPass && lowDensityJobs.length > 0)
      {
        jobManagers = lowDensityJobs;
        concurrency = 0;
        lowDensityPass = true;
        return next();
      }
      selectiveDebug2() && console.debug(`makeJobSelectionCursor:next(${concurrency},${jobIdx},${readyJobs.length},${that.workingSliceDensity}): null`, lowDensityPass);
      return null; /* Did not find any more work that fits. */
    }
    /** @param {Slice} [slice] */
    function commit (slice)
    {
      if (slice)
        slice.markAsReserved();
      if (peekPrefered)
        peekPrefered = false;
      else
        jobIdx++;
    }
    /** @param {boolean} b */
    function useGPU (b) { skipGPUJobs = !b; }

    return { next, commit, useGPU };
  }

  /**
   * Handle sandbox.work(...) errors.
   * This is the exception handler that catches exception from sandbox.work.
   * Kills sandbox and either retries the slice or transitions slice to FAILED and returns to scheduler.
   *
   * @param {Sandbox} sandbox
   * @param {Slice} slice
   * @param {Error} error
   * @returns {string}
   */
  handleSandboxWorkError (sandbox, slice, error)
  {
    if (debugBuild && !(slice.isReserved || slice.isWorking  || slice.isWorkDone || slice.isReady && sandbox.isTerminated)) // Sanity. Exception should never fire.
    {
      selectiveSupEx() && console.debug(`handleSandboxWorkError(${this.workerId},sbx=${sandbox.identifier}): slice ${slice.identifier} is in bad state ${slice.state}`, slice.state.lastStack, error);
      if (!sandbox.isTerminated)
        throw new Error(`handleSandboxWorkError: slice ${slice.identifier} must be RESERVED, WORKING or WORKDONE.`);
    }

    // Always terminate sandbox.
    this.returnSandbox(sandbox);

    /** @type {boolean} */
    const isSandboxError = error instanceof SandboxError;
    /** @type {string} */
    let reason;

    if (isSandboxError)
      reason = error['errorCode']
    else
    {
      // This error was unrelated to the work being done.
      reason = 'Slice has failed to complete execution';
      if (!error)
        error = new Error(`Slice ${slice.sliceNumber} in state ${slice.state} of job ${slice.jobId} failed to complete execution`);
    }

    if (selectiveSupEx() && (Supervisor.isReportable(error) || selectiveDebug2()))
      console.debug('handleSandboxWorkError:', slice.identifier, error);

    let errorString = '<unknown error>'; // Should never see this.
    if (error.name === 'EWORKREJECT')
    {
      this.handleWorkReject(slice);
      return 'EWORKREJECT';
    }

    if (!slice.isReady)
    {
      selectiveDebug2() && console.debug(`handleSandboxWorkError: returning slice ${slice.identifier}`);
      this.handleFailedSlice(slice, error);
      this.returnSlice(slice, common.errorCodeOrString(error, `handleSandboxWorkError: ${error.message}`));
    }

    /** @todo XXX fix this - we should not be reporting errors about jobs to the worker /wg apr 2025 */
    let knownError = true;
    switch (reason)
    {
      case 'ENOPROGRESS':
        errorString = 'No progress error in sandbox';
        break;
      case 'ESLICETOOSLOW':
        errorString = 'Slice too slow error in sandbox';
        break;
      case 'EPERM_ORIGIN':
        errorString = 'Could not fetch data; origin not allowed';
        break;
      case 'EFETCH':
        errorString = 'Could not fetch data';
        break;
      case 'EUNCAUGHT':
        errorString = 'Uncaught error in sandbox';
        break;
      case 'ENOMODULE':
        errorString = 'Module not found';
        break;
      case 'EMODULETOOBIG':
        errorString = 'Module is too big';
        break;
      default:
        knownError = false;
        errorString = 'Slice failed in sandbox';
        break;
    }

    if (slice.jobManager.displayMaxDiagInfo || false)
    {
      if (common.debugBuild)
        errorString = `${errorString}: job=${slice.jobManager.identifier}, jobName=${slice.jobManager.public.name}, slice=${slice.sliceNumber}, sandbox=${sandbox.id}`;
      else
        errorString = `${errorString}: job=${slice.jobManager.jobId}, jobName=${slice.jobManager.public.name}`;
    }

    selectiveDebug2() && console.debug(errorString, error);
    if (knownError)
      this.emitWorkerError(errorString, error, /*disectCoreError*/ true, /*displayTopOfStack*/ false);
    else
    {
      if (this.options.sandboxWorkErrorToWorkEmit)
        this.postWorkEmit(slice, 'error', error, errorString);
      this.directError(error, errorString);
    }

    return reason;
  }

  /**
   * Slice has thrown error during execution:
   * Mark slice as failed, compensate when job is dicrete, emit events.
   * @param {Slice} slice
   * @param {Error} error
   */
  handleFailedSlice (slice, error)
  {
    debugging('supervisor') && console.debug(`handleFailedSlice: ${slice.identifier}`, error);
    slice.collectResult(error, false /*success*/);

    // If the slice from a job never completes and the job id exists in the discreteJobs,
    // then we remove it to allow for another slice (from the same job) to be obtained by fetchTask
    const removeIdx = this.discreteJobs.indexOf(slice.jobId);
    if (removeIdx >= 0)
      this.discreteJobs.remove(removeIdx);
  }

  // _Idx
  //
  // returnSlices, returnSlice, directReturnSlice
  // checkForTabSuspension, tabSuspended,
  // sendReturnAllSlicesMessage, sendReturnAllSlicesBeacon
  //

  /**
   * Bulk-return multiple slices, possibly for assorted jobs.
   * Returns slices to the scheduler to be redistributed.
   * Called in the sandbox terminate handler and purgeAllWork(jobId)
   * and stopWork(forceTerminate).
   *
   * @param {Slice[]}      slices - The slice candidates to check if they can be returned to the scheduler.
   * @param {string|{message: string|false, errorCode: string}} reason - Reason for the return: 'ENOPROGRESS', 'EUNCAUGHT', 'ESLICETOOSLOW', 'EPERM_ORIGIN', 'EFETCH', 'unknown'.
   * @returns {Promise<*>} - Response from the scheduler.
   */
  async returnSlices (slices, reason)
  {
    if (!slices?.length)
      return;

    debugging('supervisor') && console.debug(`Supervisor.returnSlices(${this.state},reason=${utils.stringify(reason)}): Return slice candidates`, slices.map(slice => slice.identifier));
    slices.forEach(slice => this.worker.emit('drop-slice', { jobId: slice.jobId, sliceNumber: slice.sliceNumber, status: 'return' }));

    // Only return those slices which still exist in their respective jobManagers sliceInventory .
    const verifiedSlices = slices.filter((slice) => slice.jobManager.removeSlice(slice));
    if (verifiedSlices.length > 0)
    {
      selectiveSupEx() && console.debug(`Supervisor.returnSlices(${this.workerId}): Returning slices with reason=${utils.stringify(reason)}`, utils.compressSlices(verifiedSlices));
      return this.dcp4.sliceReturn(verifiedSlices, reason);
    }
  }

  /**
   * Return a slice to scheduler that is not in slice Inventory.
   * @param {Slice}  slice  - The slice to return.
   * @param {string} reason - The reason for returning.
   */
  directReturnSlice (slice, reason)
  {
    selectiveSupEx() && console.debug(`directReturnSlice: ${slice.identifier}`, slice.jobManager.sliceInventory.map((s) => s.sliceNumber).indexOf(slice.sliceNumber));
    return this.dcp4.sliceReturn([ slice ], reason);
  }

  /**
   * Takes a slice and returns it to the scheduler to be redistributed.
   * Usually called when an exception is thrown by sandbox.work(...) .
   * Or when the supervisor tells it to forcibly stop working.
   *
   * @param {Slice}  slice  - The slice to return to the scheduler.
   * @param {string|{message: string|false, errorCode: string}} reason - Reason for the return: ''ENOPROGRESS', 'EUNCAUGHT', 'ESLICETOOSLOW', 'EPERM_ORIGIN', 'EFETCH', 'unknown'.
   * @returns {Promise<*>}  - Response from the scheduler.
   */
  returnSlice (slice, reason) { return this.returnSlices([ slice ], reason); }

  /**
   * Either send an HTTP POST or when on a browser, send a beacon that lets the scheduler know that
   * we want active slices returned that are owned by this worker,
   * A beacon is a synchronous message w/o response.
   * Note: node 21 added a navigator object so someday node might have navigator.sendBeacon.  Hence
   * we don't really care whether we're on browser or not, only that navigator.sendBeacon is valid.
   */
  async sendReturnAllSlicesMessage ()
  {
    selectiveDebug2() && console.debug('sendReturnAllSlicesMessage:', Boolean(this.returnAllSlicesMessage));
    if (!this.returnAllSlicesMessage) // Worker killed before completing Sup.work() (viz., worker.start())
      return;
    for (let k = 0; k < 3; k++)
    {
      const fetchState = new FetchState(FetchState.default.returnAllSlices)
      const request = {
        paymentAddress: this.options.paymentAddress,
        fetchState: fetchState.valueOf(),
        returnAllSlices: this.returnAllSlicesMessage,
      };
      try
      {
        await this.dcp4.taskDistributor.keepalive();
      }
      catch (error)
      {
        selectiveSupEx() && console.debug('sendReturnAllSlicesMessage: Keepalive failed for task-distributor', error);
        // Don't display 'EPIPE','ECONNRESET','ECONNREFUSED','ECONNABORTED','ETIMEDOUT','ESANDBOX', errors.
        if (!Supervisor.isReportable(error))
          this.warning('Failed to connect to task-distributor.', 'Will try again at next fetch cycle.');
        if (k < 2)
        {
          // DO NOT SLEEP FOR 2 HOURS; 3 seconds is enough
          await utils.a$sleepMs(3 * 1000);
          continue;
        }
      }
      selectiveDebug() && console.debug('sendReturnAllSlicesMessage: send special request to TD', request);
      return this.dcp4.taskDistributor.request('requestTask@3', request);
    }
  }

  /**
   * Send a beacon (An HTTP message for which no response is required) that lets the scheduler know
   * that we want active slices of current worker returned. This function can only be called in a
   * browser-like environment -- specifically navigator.sendBeacon must be defined.
   */
  sendReturnAllSlicesBeacon ()
  {
    if (!this.returnAllSlicesMessage) // Worker killed before completing Sup.work() (viz., worker.start())
      return;
    const location = `${dcpConfig.scheduler.services.taskDistributor.location.href}worker-unload-beacon`;
    if (typeof navigator === 'object' && navigator.sendBeacon)
    {
      navigator.sendBeacon(location, this.returnAllSlicesMessage);
      // Notify worker to call management.updateRunInfo with status: 'pagehide'
      return management.updateRunInfo(this, { status: 'pagehide' });
    }
    this.warning('Cannot call sendReturnAllSlicesBeacon() unless navigator.sendBeacon is properly defined.');
  }

  /**
   * Set this.detectedTabSuspended = true and reset to false when timer expires.
   * When called with a timer running, clear timer and start new one.
   */
  tabSuspended ()
  {
    this.detectedTabSuspended = true;
    if (this.detectedTabTimer)
      dcp_timers.clearTimeout(this.detectedTabTimer);
    this.detectedTabTimer = dcp_timers.setTimeout(() => {
      this.detectedTabSuspended = false;
      this.detectedTabTimer = null;
    }, (this.options.progressTimeoutMs + 1000) * common.timeDilation);
    this.detectedTabTimer.unref();
  }

  /**
   * Called by setInterval. This method will measure the time span between its calls. If the
   * actual interval between invocations is longer than detectSuspensionLateMultiplier * detectSuspensionIntervalMs
   * the JS context is considered to be suspended. If suspension is detected, a tabSuspended event is emitted.
   */
  checkForTabSuspension ()
  {
    const now = Date.now();
    if (this.lastSuspensionDetectionCheck)
    {
      const actualIntervalMs = now - this.lastSuspensionDetectionCheck;
      if (actualIntervalMs > this.options.detectSuspensionLateMultiplier * this.options.detectSuspensionIntervalMs)
        this.tabSuspended();
    }
    this.lastSuspensionDetectionCheck = now;
  }

  // _Idx
  //
  // workRemaining, repoMan, predictLoad(viz., clairvoyance), waitUntilWorkIsReady
  //

  /**
   * Compute the estimated time to completion of all work.
   * The time is measured as if there were only a single slice running at a time.
   * workRemaining is the amount of time until completion.
   * @returns {number}
   */
  workRemaining ()
  {
    let workRemaining = 0;
    for (const jobMan of this.jobManagerInventory)
      workRemaining += jobMan.workRemaining();
    return workRemaining;
  }

  /**
   * @todo XXXpfr Should we not schedule long slices to a worker with too low defaultQuanta?
   *
   * When the estimated time to completion of all work is more than
   *   repoManMultiplier * targetTaskDuration * this.maxWorkingCores,
   * return slices until the excess is removed.
   * Be fair. Round-robin over all jobs until excess is eliminated.
   * Kill the long jobs 1st.
   */
  repoMan()
  {
    if (!this.isReadyForWork)
      return;

    const threshold = this.options.repoManMultiplier * this.options.targetTaskDurationMs * this.maxWorkingCores;
    const workRemaining = this.workRemaining();
    let excess = workRemaining - threshold;
    selectiveDebug2() && console.debug(`repoMan: excess ${excess}, workerRemaining ${workRemaining}, threshold ${threshold}`);
    if (excess > 0)
    {
      const slices = [];
      /** @param {JobManager[]} jmi */
      function returnFrom(jmi)
      {
        let _excess;
        do
        {
          _excess = excess;
          for (const jobMan of jmi)
          {
            const _readySlices = jobMan.readySlices;
            if (_readySlices.length > 0)
            {
              const slice = _readySlices[0];
              slice.repoMan(); // Mark as FINISHED
              slices.push(slice);
              excess -= jobMan.adjElapsedTime;
              if (excess <= 0)
                break;
            }
          }
        } while (_excess !== excess && excess > 0);
      }
      // Be fair. Round-robin over all jobs until excess is eliminated.
      // Except the long jobs are killed 1st.
      const longJobs = this.jobManagerInventory.filter((jobMan) => jobMan.emaCPUTime >= this.options.targetTaskDurationMs);
      if (longJobs.length > 0)
        returnFrom(longJobs);
      if (excess > 0)
        returnFrom(this.jobManagerInventory);
      selectiveDebug() && (slices.length > 0) &&  console.debug(`repoMan: excess ${excess}, workerRemaining ${workRemaining}, threshold ${threshold}, returned-slice-count ${slices.length}`);
      this.returnSlices(slices, 'repoMan');
    }
  }

  /**
   * NOTE: NOT PERFECT YET WRT CPU vs GPU DENSITY!!!!!!
   * Predict the total reduction in density of working sandboxes timeSpanMs from now.
   * This function is called right before fetchTask, in order to calculate how much space is available.
   * @returns {{ queued: Slice[], working: number }}
   */
  predictLoad()
  {
    const timeSpanMs = this.options.prefetchIntervalMs;
    const queued = [];
    let working = 0;
    for (const jobMan of this.jobManagerInventory)
    {
      const { queued: jmQueued, working: jmWorking } = jobMan.predictLoad(timeSpanMs);
      queued.push(...jmQueued);
      // Optimize to short-circuit when queued > 1, because we won't call fetchWork in that case.
      if (queued.length > 1)
        break;
      working += jmWorking;
    }
    selectiveDebug() && console.debug(`Supervisor.predictLoad: queued ${queued.length}/${this.queuedSlices.length}, working ${working}/${this.workingSlices.length}`)
    return { queued, working };
  }

  /**
   * On the first call to fetchTask
   *  or when the last call to fetchTask found nothing,
   *  or when there are no ready slices,
   * wait until at least 1 job is ready with at least 1 ready slice.
   * @param {Array<Promise<any>>} jobManagerPromises
   * @returns {Promise<any>}
   */
  waitUntilWorkIsReady (jobManagerPromises)
  {
    if (this.waitForWork)
    {
      debugging('supervisor') && console.debug(`waitUntilWorkIsReady: promise count ${jobManagerPromises?.length}`, jobManagerPromises);
      this.waitForWork = false;
      // Promise.any is supported in Node 15, Chrome 85, Edge 85, Firefox 79, Safari 14, Opera 71.
      // It was implemented in node and browsers in 2nd half of 2020, so there's a chance many
      // customers will not have browsers that support it. Though currently (May 2025) DCP uses node 18 or 20.
      return Promise_any(jobManagerPromises);
    }
    // Flush microtask queue
    return utils.a$sleepMs(0);
  }

  // _Idx
  //
  // availableSandboxSpace, fetchTask, createTDRequest, addTaskToWorkload, fetchFromTD, clearUnusedJobs
  //

  /**
   * Returns the number of unused sandbox slots to fill -- sent to fetchTask.
   * @param {Slice[]} queued
   * @param {number}  working
   * @returns {number}
   */
  availableSandboxSpace (queued, working)
  {
    // If we find more than 1 queued slices, bail early.
    if (queued.length > 1)
      return 0; // We have more than 1 ready slices, no need to fetch.

    let longSliceCount = 0;
    if (queued.length < 1)
      this.waitForWork = true; // There are no ready slices.
    else if (queued[0].isLong)
      longSliceCount = 1;

    // There are almost no ready slices (there may be 0 or 1), fetch a full task.
    // The task is full, in the sense that it will contain slices whose
    // aggregate execution time is roughly this.maxWorkingCores * 3-minutes.
    // However, there can only be this.maxWorkingCores # of long slices on a worker,
    // Thus we need to know whether the last slice in this.readySlices() is long or not.
    // (A long slice has estimated execution time >= 3-minutes or is an estimation slice.)

    const numCores = this.maxWorkingCores - working - longSliceCount;
    selectiveDebug2() && console.debug('availableSandboxSpace', numCores, working, longSliceCount);
    return numCores;
  }

  /**
   * Method of Supervisor which validates options used to request tasks. Returns 'true' to give the
   * supervisor the green light to fetch. Returns a string explaining the problem when there is no error
   * but fetching would be pointless. Throws Error when there is an error that is probably the result of
   * the worker-developer or sysop making a mistake.
   */
  #validateTaskRequestOptions()
  {
    if (this.options.jobIds && !Array.isArray(this.options.jobIds))
      throw new Error('jobIds must be an Array');
    if (!this.options.jobIds && this.options.jobIds !== false)
      throw new Error(`jobIds has incorrect value ${this.options.jobIds}`);

    if (this.options.jobIds['length'] === 0)
      return 'jobIds is an empty Array';
    if (this.options.cores.cpu === 0)
      return 'cores.cpu = 0';
    if (this.options.maxSandboxes === 0)
      return 'maxSandboxes = 0';

    return true;
  }

  /**
   * Ask the scheduler (task distributor) for work (Rq).
   * @returns {Promise<*>}
   */
  async fetchTask ()
  {
    if (!this.isReady)
      return;
    // We still want to enter when this.evaluator.reallyDown is true, because we want to call
    // spinCreateSandbox in the critical region specified by fetchTaskBarrier.

    // Can't do much if we can't create a sandbox
    if (this.options.maxSandboxes < 1)
      return;

    /**
     * Valid states normal, notifySoteria, startupCheck, roundRobin.
     * Multiple state can be true at the same time, e.g. normal, notifySoteria amd roundRobin.
     *   normal:        Fetch a task from TD
     *   notifySoteria: Update 'activeSlices' table for all slices already owned by worker
     *   startupCheck:  At worker startup, check activeSlices for any owned slices and mark them as 'returned'
     *   roundRobin:    Be sure to call Supervisor.roundRobinSlices during the fetchTask processing.
     * Rules:
     * 1) notifySoteria and startupCheck cannot both be true
     * 2) normal is false when there isn't enough unused core space or waitingToCreateSandbox
     * 3) notifySoteria will forced false, when there are no owned slices
     * @type {FetchState}
     */
    const fetchState = new FetchState();

    const now = Date.now();
    const notifySoteria = !this.fetchTaskBarrier && (now - this.lastSoteriaRequest >  this.options.soteriaFrequencyMs);
    if (notifySoteria)
    {
      // At startup, Soteria checks for orphaned slices belonging to this worker.
      if (!this.runOnceAtStartup)
        fetchState.notifySoteria = true;
      else
      {
        fetchState.startupCheck = true;
        this.runOnceAtStartup = false;
      }
    }

    // Every 60 seconds check to see if the estimated time to completion of all work is more than
    //   repoManMultiplier * this.options.targetTaskDurationMs * this.maxWorkingCores,
    // and then return slices until the excess is removed.
    // Be fair. Round-robin over all jobs until excess is eliminated. Kill the long jobs 1st.
    if (now > this.lastRepoMan + this.options.repoManFrequencyMs)
    {
      this.lastRepoMan = now;
      this.repoMan();
    }

    // There are 2 barriers wrt fetchTask,
    // 1) fetchTaskBarrier is a barrier for the task fetching from task distributor path.
    // 2) roundRobinBarrier is a barrier for the slice execution path.
    try
    {
      const { queued, working } = this.predictLoad();
      selectiveDebug() && console.debug(`fetchTask::PredictLoad(${now-this.lastTime}): queued ${queued.length}, working ${working}, workingSliceCount ${this.workingSliceCount}, workingDensity ${this.workingSliceDensity}, workingSandboxCount ${this.workingSandboxCount}, maxSboxes ${this.maxWorkingCores}`);

      const cpuCoreSpace = this.availableSandboxSpace(queued, working);
      selectiveDebug2() && console.debug(`Supervisor.fetchTask: cpuSpace ${cpuCoreSpace}, waiting-on-sbx ${this.evaluator.waitingToCreateSandbox}, queued ${queued.length}, working ${working}`, notifySoteria, this.fetchTaskBarrier, Math.min(1, this.cores.cpu * common.schedulingSlop));

      // This says to not query TD unless we need to fill at least 1 core.
      // Trying to get TD to fill up fractional cpuCoreSpace < 1, could cause excessive TD traffic
      // returning empty tasks, when there aren't any jobs whose cpu density can fit into that fraction.
      if (cpuCoreSpace < 1)
      {
        selectiveDebug2() && console.debug(`Supervisor.fetchTask(${now-this.lastTime}): Execute slices instead of fetching a task: cpuSpace ${cpuCoreSpace}, queued ${queued.length}, working ${working}, waiting-for-sandbox ${ this.evaluator.waitingToCreateSandbox}`);
        if (!notifySoteria)
          return this.roundRobinSlices();
        fetchState.normal = false;
        fetchState.roundRobin = true;
      }

      const vtroResult = this.#validateTaskRequestOptions();
      if (vtroResult !== true)
      {
        selectiveDebug2() && console.debug(`fetchTask: not fetching due to ${vtroResult}`);
        return;
      }

      selectiveDebug() && console.debug(`fetchTask(${now-this.lastTime}, ${this.fetchTaskBarrier}) begin q/w/cpuSpace/slots/space/future-space`, queued.length, working, cpuCoreSpace, this.unusedSandboxCount, this.unusedCoreSpace);

      if (this.fetchTaskBarrier)
        return;
      // fetchTaskBarrier is a barrier for the task fetching from task distributor path.
      // When fetchTaskBarrier is true, notifySoteria must be false, which prevents excessive
      // Soteria notifications.
      this.fetchTaskBarrier = true;

      // Initialize this.fetchState with the local state.
      // It's only safe to modify this.fetchState when it's in the critical region specified by:
      //    this.fetchTaskBarrier = true;
      this.fetchState = new FetchState(fetchState.valueOf());

      // When this.capabilities isn't initialized, it means we've never created a sandbox before.
      // We'd better create one so we can ask TD for a task.
      //
      // When this.evaluator.reallyDown is true, call spinCreateSandbox.
      // Since we're in the fetchTaskBarrier critical region, any further calls to fetchTask will
      // exit above.  While pruneSandboxes, repoMan and roundRobinSlices could be called,
      // they won't do anything because reallyDown means this.isReadyForWork is false which short circuits
      // the functions. Moreover, since this.isReadyForWork is false, it helps prevent a pending TD request
      // from being sent.
      //
      // Why do we want to spinCreateSandbox?
      // Because we need to poll StandaloneWorker to find out whether we can create sandboxes again.
      // Screensaver worker stops the evaluator when the screensaver goes down.
      // Spinning/polling until we can create a sandbox is currently the only way to tell whether the
      // evaluator is up again.
      //
      // Since all slices have been returned, when reallyDown is true, we don't need to do Soteria requests.
      //
      if (this.evaluator.reallyDown || !this.capabilities)
      {
        selectiveDebug2() && console.debug('fetchTask: Calling spinCreateSandbox:', String(this.state), this.evaluator.createSandboxRefCount, this.sandboxInventory.map((sbx) => sbx.identifier));
        await this.spinCreateSandbox();
        selectiveDebug2() && console.debug('fetchTask: Finished spinCreateSandbox:', String(this.state), this.evaluator.createSandboxRefCount, this.sandboxInventory.map((sbx) => sbx.identifier));
      }

      // Record fetch start time.
      this.fetchTaskStarted = now;

      // Call Task Distributor and handle response with this.addTaskToWorkload.
      return this.fetchFromTD(cpuCoreSpace, (request, response) => this.addTaskToWorkload(request, response))
        .catch((error) => {
          // Should never get here.
          this.fetchTaskBarrier = false;
          this.directFatalError(error, 'Supervisor.fetchFromTD failed!');
        }).finally(() => { this.fetchTaskBarrier = false; });
    }
    catch (error)
    {
      this.fetchTaskBarrier = false;
      this.directFatalError(error, 'Supervisor.fetchTask failed!');
    }
  }

  /**
   * Create jobId -> sliceNumbers for non-finished slices.
   * @returns {Object<string, number[]>}
   */
  soteriaJobSlices ()
  {
    let hasSoteriaSlices = false;
    /** @type {Object<string, number[]>} */
    const soteriaJobs = {};
    for (const jobManager of this.jobManagerInventory)
    {
      if (jobManager.ready || jobManager.initial)
      {
        const sliceNumbers = jobManager.sliceNumbers;
        if (sliceNumbers.length > 0)
        {
          soteriaJobs[jobManager.jobId] = sliceNumbers;
          hasSoteriaSlices = true;
        }
      }
    }
    if (!hasSoteriaSlices)
      return null;
    selectiveSupEx() && console.debug('soteriaJobSlices:', soteriaJobs);
    return soteriaJobs;
  }

  /**
   * @todo XXXpfr Do estimation slices here too.
   * Finds overdue working slices for Soteria to mark as overdue in activeSlices.
   * Returns late unassigned slices.
   * Returns late reserved slices.
   * @returns {Object<string, number[]>}
   */
  checkForOverdueSlices ()
  {
    let hasOverdueSlices = false;
    /** @type {Object<string, number[]>} */
    const overdueSlices = {};
    const reservedThreshold = this.options.reservedSliceLifetimeMs;
    const artifactResolutionThreshold = this.options.artifactResolutionTimeoutMs;
    const notifySlow = 2 * 60 * 1000;
    const now = Date.now();
    const lateReservedSlices = [];
    let lateUnassignedSlices = [];
    for (const jobManager of this.jobManagerInventory)
    {
      if (jobManager.initial) // Has not resolved artifacts yet.
      {
        const currentFetchTime = now - jobManager.fetchArtifactTime;
        if (currentFetchTime > notifySlow && common.highPriorityDiag())
          this.warning(`Job ${jobManager.identifier} is taking more than ${Math.floor(currentFetchTime*1e-3)}s to resolve artifacts, will purge job at ${Math.floor(artifactResolutionThreshold*1e-3)}s`);
        if (currentFetchTime > artifactResolutionThreshold)
        {
          common.highPriorityDiag(true) && this.warning(`Could not fetch artifacts for job ${jobManager.identifier} within ${Math.floor(artifactResolutionThreshold*1e-3)}s; purging job from worker`);
          this.purgeJob(jobManager, `Cannot resolve job dependencies within ${Math.floor(artifactResolutionThreshold*1e-3)}s`);
        }
      }

      // Estimation slices have threshold === -1, because we don't do local Soteria for estimation slices yet.
      // When !useLifetimeAnalysis, we don't check for overdue working slices.
      // Always return a reserved slice when it is older than reservedThreshold.
      // Always return an unassigned slice when it is older than artifactResolutionThreshold.
      let threshold = this.options.useLifetimeAnalysis ? jobManager.computeOverdueThreshold() : -2;
      if (threshold >= 0 && threshold < this.options.targetTaskDurationMs)
        threshold = this.options.targetTaskDurationMs; // threshold (>= 0) must be at least targetTaskDurationMs.

      for (const slice of jobManager.sliceInventory)
      {
        const elapsedTime = now - slice.startTime;
        if (slice.isWorking)
        {
          const sliceThreshold = threshold * (slice.cotaskCount + 1); // Either estimation or !useLifetimeAnalysis has sliceThreshold < 0
          if (!slice.overdue && elapsedTime > sliceThreshold && sliceThreshold >= 0)
          {
            selectiveDebug2() && console.debug(`checkForOverdueSlices(${slice.identifier}): now-startTime=${elapsedTime}, sliceThreshold=${sliceThreshold}`);
            slice.overdue = (hasOverdueSlices = true);
            if (!overdueSlices[jobManager.jobId])
              overdueSlices[jobManager.jobId] = [ slice.sliceNumber ];
            else
              overdueSlices[jobManager.jobId].push(slice.sliceNumber);
          }
        }
        else if (slice.isReserved) // late transition RESERVED --> WORKING
        {
          if (elapsedTime > reservedThreshold)
            lateReservedSlices.push(slice);
          if (elapsedTime > notifySlow && common.highPriorityDiag())
            this.warning(`Slice ${slice.identifier} is taking more than ${Math.floor(elapsedTime*1e-3)}s to start WORKING; will return slice at ${Math.floor(reservedThreshold*1e-3)}s`);
        }
        else if (slice.isUnassigned)
        {
          if (elapsedTime > artifactResolutionThreshold) // late transition INITIAL --> READY
            lateUnassignedSlices.push(slice);
          if (elapsedTime > notifySlow && common.highPriorityDiag())
            this.warning(`Slice ${slice.identifier} is taking more than ${Math.floor(elapsedTime*1e-3)}s to resolve data; will return slice at ${Math.floor(artifactResolutionThreshold*1e-3)}s`);
        }
      }
    }
    if (lateReservedSlices.length > 0)
    {
      common.highPriorityDiag() && this.warning(`checkForOverdueSlices: returning late reserved slices ${lateReservedSlices.map((s) => s.identifier)}`);
      this.returnSlices(lateReservedSlices, 'late reserved slices');
    }
    if (lateUnassignedSlices.length > 0)
    {
      common.highPriorityDiag() && this.warning(`checkForOverdueSlices: returning late unassigned slices, ${lateUnassignedSlices.map((s) => s.identifier)}`);
      this.returnSlices(lateUnassignedSlices, 'late unassigned slices');
    }
    if (!hasOverdueSlices)
      return null;
    selectiveSupEx() && console.debug(`checkForOverdueSlices(${this.workerId}):`, utils.stringify(overdueSlices));
    return overdueSlices;
  }

  /**
   * Create request for TD.requestTask .
   * @param {number} cpuCoreSpace
   * @returns {object}
   */
  createTDRequest (cpuCoreSpace)
  {
    /* @todo XXXpfr Think about how to do targetLoad.longSlices better.
     * Ideas:
     * 1) While branchy Javascript is CPU bound, that doesn't mean hyperthreading isn't useful.
     *    When a branch is mispredicted the whole CPU instruction pipeline is flushed, which is
     *    a huge perf hit and while waiting to fill the pipeline again, a hyperthread can get
     *    a whole bunch of work done.
     * 2) Setting the Sup2.cores.cpu to #lCores is probably too much, but I've had great success with
     *       Sup2.cores.cpu = Math.ceil(1.5 * #pCores)
     *    Which is very close to optimal throughput of work done.
     * 3) When the scheduler is 1/2 very long slices, the short slices will tend to get starved.
     *    The config property dcpConfig.scheduler.preventSliceStarvation when set to true (default false)
     *    will always leave one vCore open for short slices in every worker. In the future, I want the
     *    scheduler to detect short slice starvation and dynamical turn preventSliceStarvation on until
     *    short slice starvation is alleviated and then turn it back off.
     * 4) maxSandboxes is currently set to
     *       factor * Sup2.cores.cpu
     *    where 1.2 <= factor <= 1.5 depending upon how many lCores a worker has. Where I assume that a
     *    machine with a large number of lCores has sufficient memory to handle a bigger factor.
     *    The factor boundaries can be adjusted in dcpConfig.supervisor, but I intend to also allow them
     *    to be overridden at the dcpConfig.worker level, so if somebody has a 32 lCore machine with
     *    8GB of RAM they can adjust factor to be closer to 1. Ideally we could adjust the factor
     *    boundaries at the job/CG level.
     */
    // Only notify Soteria when there are loaded jobs or worker just started,
    // or there are overdue slices.
    let overdueSlices = null, soteriaJobs = null;
    if (this.fetchState.notifySoteria)
    {
      //this.showSlices('createTDRequest'); // SAVE
      soteriaJobs = this.soteriaJobSlices();
      overdueSlices = this.checkForOverdueSlices();
      // Don't need to check for startupCheck=false because it must be when notifySoteria=true.
      assert(!this.fetchState.startupCheck);
      // Don't need to check for !overdueSlices because !soteriaJobs implies !overdueSlices
      assert(soteriaJobs || !overdueSlices);
      if (!soteriaJobs)
      {
        selectiveDebug() && console.debug('createTDRequest: No need to notify Soteria.');
        this.fetchState.notifySoteria = false;
        if (this.fetchState.roundRobin && !this.fetchState.normal)
          this.roundRobinSlices();
      }
    }
    if (selectiveDebug() && (false || overdueSlices || this.fetchState.valueOf() !== 1))
      console.debug(`createTDRequest(${Date.now() - this.lastTime}): fetchState=${this.fetchState.valueOf()}, workerId=${this.workerId}, overdueSlices=${utils.stringify(overdueSlices)}, soteriaFrequencyMs=${this.options.soteriaFrequencyMs}`);

    if (!this.capabilities)
      throw new Error(`Sandbox capabilities has not been computed.`);

    let request =  null;
    if (this.fetchState.normal)
    {
      request = {
        targetLoad:           { cpu: cpuCoreSpace, gpu: this.maxWorkingGPUs, longSlices: Math.floor(cpuCoreSpace) },
        coreStats:            this.options.getStatisticsCPU(),
        jobQuanta:            this.quanta.calculateJobQuanta(),
        capabilities:         capabilities.toBits(this.capabilities),
        gpuSpeed:             this.webGPUBenchmarkMs,
        paymentAddress:       this.options.paymentAddress,
        jobIds:               this.options.jobIds || [], // When jobId.length > 0, only fetches slices for these jobs.
        workerComputeGroups:  null, // Will be fixed-up in 'beforeSend' event handler in fetchFromTD.
        minimumWage:          this.options.minimumWage,
        soteriaJobs,
        overdueSlices,
        previouslyWorkedJobs: this.discreteJobs.buf, // previouslyWorkedJobs is a misleading name
        rejectedJobs:         this.rejectedJobs.buf,
        unresolvedJobs:       this.unresolvedJobs, // Still downloading/resolving artifacts, don't fetch for these jobs until resolved.
        fetchState:           this.fetchState.valueOf(),
        worktimes:            this.worktimes,
        seq:                  this.fetchSeq++,
      };
    }
    else if (this.fetchState.notifySoteria)
    {
      request = {
        coreStats:      { worker: this.workerId },
        paymentAddress: this.options.paymentAddress,
        soteriaJobs,
        overdueSlices,
        fetchState:     this.fetchState.valueOf(),
      };
    }

    if (this.fetchState.startupCheck)
      request['returnAllSlices'] = this.returnAllSlicesMessage;
    selectiveDebug2() && console.debug(`createTDRequest(${Date.now() - this.lastTime}): sot=${this.fetchState.notifySoteria}, fetchState=${request?.fetchState}, workerId=${this.workerId}, overdueSlices=${utils.stringify(overdueSlices)}`, request);

    return request;
  }

  /**
   * Callback for fetchFromTD.
   * @param {object} request
   * @param {object} response
   */
  async addTaskToWorkload (request, response)
  {
    const constructFetchHandle = (size, jobs, slices) => {
      return {
        fetchStart: this.fetchTaskStarted,
        fetchEnd: Date.now(),
        fetchSize: size,
        jobs,
        slices,
      };
    };
    /**
     * jobs: object map: jobId -> sliceNumber[] from slices with jobId
     * @todo Try to talk Wes into using the 'fetch2' pattern for 'fetch' or combine the two.
     * @param {number} size
     * @param {Object<string, { jobHandle: EventEmitter, slices: number[] }>} jobs
     * @returns {{ fetchStart: number, fetchEnd: number, fetchSize: number, jobs: Object<string, { jobHandle: EventEmitter, slices: number[] }>, fetchState: number }}
     */
    const constructFetchHandle2 = (size, jobs) => {
      return {
        fetchStart: this.fetchTaskStarted,
        fetchEnd: Date.now(),
        fetchSize: size,
        jobs,
        fetchState: request.fetchState,
      };
    };

    try
    {
      assert(response.success && Boolean(response.payload)); // Failure handled in fetchFromTD after TD request.

      /** @type {TDPayload} */
      const payload = response.payload;
      // It's possible for payload to be undefined if an exception is thrown at just the right time.
      if (!payload.body?.newJobs) // No slices found.
      {
        // Reset first fetch logic.
        this.waitForWork = true;
        /**
         * The 'fetch' event fires even when the task distributor found no work.
         * @link https://gitlab.com/Distributed-Compute-Protocol/dcp-docs-wes/-/blob/wip/worker/worker-events.md
         * @event Supervisor#fetch
         */
        this.worker.emit('fetch', constructFetchHandle(0, {}, {}));
        selectiveDebug2() && console.debug('Task distributor found no slices...');
        // There may be an existing slice to process.
        return this.roundRobinSlices();
      }

      /** @todo XXXpfr At this point the line #'s are short by 42 -- figure out why. */
      /**       XXXwg AFAICT it's because of requireNative and a webpack bug, I asked Ryan R. to investigate but never found a solution */

      /*
       * payload:   { TDPayload }
       * TDPayload: { owner: Address, signature: Signature, auth: Auth, body: Body };
       * Auth:      { workerId: string, authSlices: Object<string, number[]>, schedulerId: { jobId: opaqueId }, jobCommissions: Object<string, { rate: number, account: number }> }
       * Body:      { newJobs: Object<string, object>, task: Object<string, SliceMessage[]>, computeGroupOrigins: Object<string, Object<string, string[]>>, schedulerConfig: {{ shared: *, targetTaskDuration: number }} }
       *
       * NOTE: authorizationMessage has type AuthMessage
       */

      const { body,  ...authorizationMessage } = payload;
      const { newJobs, task, schedulerConfig } = body;
      const newJobKeys = Object.keys(newJobs);
      const jobCount = newJobKeys.length;
      const jobSliceMap = task;

      if (schedulerConfig)
      {
        this.options.soteriaHive = new wd.SoteriaSharedHive(schedulerConfig);
        selectiveDebug2() && console.debug(`Supervisor: soteriaFrequencyMs=${1e-3 * this.options.soteriaFrequencyMs}s`);
      }

      /*
      * Ensure all jobs received from the scheduler (task distributor) are:
      * 1. If we have specified specific jobs the worker may work on, the received jobs are in the specified job list
      * 2. If we are in localExec, at most 1 unique job type was received (since localExec workers are designated for only one job)
      * If the received jobs are not within these parameters, stop the worker since the scheduler cannot be trusted at that point.
      */
      if (request.jobIds?.length > 0 && !newJobKeys.every((ele) => request.jobIds.includes(ele)))
      {
        // "fetchTask:" because that should make sense to somebody that doesn't know the internals of Supervisor.
        this.emitFatalError("fetchTask: Worker received slices it shouldn't have; rejecting the work and stopping.");
        this.stopWork(true);
        return;
      }

      /** @todo XXXpfr Figure out how not to construct this every time. */
      this.jobMap = {};
      this.jobManagerInventory.forEach(jobManager => {
        this.jobMap[jobManager.jobCgId] = jobManager;
      });

      selectiveDebug() && console.debug(`addTaskToWorkload(${Date.now() - this.lastTime},${this.workerId}): newJobs ${newJobKeys}, jobSliceMap ${common.compressJobMap(jobSliceMap, (s) => s.sliceNumber)}`);
      selectiveDebug2() && console.debug('addTaskToWorkload: Before loading task; job states', this.jobManagerInventory.map((jm) => jm.identifier));

      let sliceCount = 0;
      /** @type {Array<Promise<*>>} */
      const jobManagerPromises = [];
      const jobs = {}, slices = {};
      /** @type {Object<string, { jobHandle: EventEmitter, slices: number[] }>} */
      const fetch2Jobs = {};

      // Populate the originManager with allowed origins for compute groups it's in
      if (this.options.trustScheduler)
      {
        for (const [computeGroupId, origins] of Object.entries(body.computeGroupOrigins))
          for (const purpose in origins)
            for (const url of origins[purpose])
              this.originManager.add(url, purpose, computeGroupId);
      }

      // Populate the job managers with slices, creating new job managers when necessary.
      // Set up discrete job ring buffer.
      for (const [jobId, jobMessage] of Object.entries(newJobs))
      {
        assert(jobId);
        assert(jobMessage);
        /** @type {JobManager} */
        let jobManager;
        const sliceMessages = jobSliceMap[jobId];
        sliceCount += sliceMessages.length;

        if (this.jobMap.hasOwnProperty(jobMessage.jobCgId))
        {
          jobManager = this.jobMap[jobMessage.jobCgId];
          jobManager.update(jobMessage, sliceMessages, authorizationMessage);
        }
        else
        {
          if (!jobMessage.codeLocation)
            throw new Error(`addTaskToWorkload(${jobId},${this.workerId}): Received invalid jobMessage from Task Distributor: ${utils.stringify(jobMessage, 1024, 2)}`);

          /**
           * Do not fetch slices from any unresolved job managers.
           * Normally the job artifacts should get reolved quickly. When JM.fetchDependencies succeeds
           * the correspnding address is removed from unresolvedJobs and the job manager is inserted
           * in Sup.jobManagerInventory. We wait up to artifactResolutionTimeoutMs (e.g. 45 minutes)
           * for artifact resolution. If a job does not resolve within artifactResolutionTimeoutMs then
           * the whole job is returned.  If a slice does not resolve within artifactResolutionTimeoutMs
           * then the slice is returned.
           * @todo Investigate soft-rejecting the whole job if job-artifacts not reolved with 5 minutes.
           * Because of the delay in recognizing how many slices we have from unresolved jobs, we
           * may end up with more slices than we should, but repoMan will take care of that.
           */
          this.unresolvedJobs.push(jobId);

          // Add the slice messages to the job manager ctor, so that slice construction is after job manager is ready.
          jobManager = new JobManager(this, jobMessage, sliceMessages, authorizationMessage);
          this.jobMap[jobMessage.jobCgId] = jobManager;

          // Populate the ring buffer based on job's discrete property.
          if (jobMessage.discrete && !this.discreteJobs.find(id => id === jobId))
            this.discreteJobs.push(jobId);
        }
        jobs[jobId] = jobManager.jobHandle;
        slices[jobId] = task[jobId].length;
        fetch2Jobs[jobId] = { jobHandle: jobManager.jobHandle, slices: [ ...(authorizationMessage.auth.authSlices[jobId]) ] };

        jobManagerPromises.push(jobManager.jobPromise);
      }

      // Clear out job managers w/o any queued slices,
      // and remove corresponding job references from module cache.
      // When a cached module no longer has any job references it is removed from the cache.
      this.clearUnusedJobs(newJobs);

      const payloadLength = kvin.stringify(payload).length; /** @todo - fix per DCP-3750 */
      /**
       * The 'fetch' event fires when the supervisor has found work from the task distributor.
       * @link https://gitlab.com/Distributed-Compute-Protocol/dcp-docs-wes/-/blob/wip/worker/worker-events.md
       * @event Supervisor#fetch
       */
      this.worker.emit('fetch', constructFetchHandle(payloadLength, jobs, slices));
      /** @todo Try to talk Wes into using the 'fetch2' pattern for 'fetch' or combine the two.
       * The fetch2 payload is:
       * {
       *   fetchStart: number;
       *   fetchEnd:   number;
       *   fetchSize:  number;
       *   jobs: {
       *     [x: string]: {
       *       jobHandle: EventEmitter;
       *       slices: number[];
       *   };
       * };
       */
      this.worker.emit('fetch2', constructFetchHandle2(payloadLength, fetch2Jobs));

      const compressTask = () => { return common.compressJobMap(authorizationMessage.auth.authSlices); }
      selectiveSupEx() && console.debug(`addTaskToWorkload(${Date.now() - this.fetchTaskStarted},${Date.now() - this.lastTime},${this.workerId}): cpuCoreSpace: ${request.targetLoad.cpu}, task: ${sliceCount}/${request.targetLoad.cpu}/${this.maxWorkingCores}, jobs: ${jobCount}, authSlices: ${compressTask()}, conversion:`, request.jobQuanta);

      // On the first call to fetchTask,
      //  or when the last call to fetchTask found nothing,
      //  or when there are no ready slices,
      // wait until at least 1 job with 1 slice is ready.
      await this.waitUntilWorkIsReady(jobManagerPromises);

      selectiveDebug2() && console.debug('addTaskToWorkload: Before calling roundRobinSlices; job states', this.jobManagerInventory.map((jm) => jm.identifier));

      // Start working on the new slices.
      return dcp_timers.setImmediate(() => this.roundRobinSlices());
    }
    catch (error)
    {
      this.worker.emit('fetch', error);
      this.directFatalError(error, 'Supervisor.addTaskToWorkload failed!');
    }
    finally
    {
      this.fetchTaskBarrier = false; // Redundant, but keep it anyway.
    }
  }

  /**
   * @private
   * @callback cbAddTaskToWorkload
   * @param {*} request
   * @param {*} response
   * @returns {Promise<void>}
   */

  /**
   * Call to fetch new slices from task distributor.
   * @param {number} cpuCoreSpace
   * @param {cbAddTaskToWorkload} addTaskToWorkload
   * @returns {Promise<any>}
   */
  async fetchFromTD (cpuCoreSpace, addTaskToWorkload)
  {
    const that = this;

    selectiveDebug2() && console.debug(`fetchFromTD(${Date.now()-this.lastTime}) begin; BarrierState:`, this.fetchTaskBarrier, this.roundRobinBarrier);
    // Fetch a new task if we have insufficient slices queued, then start workers
    if (!this.fetchTaskBarrier)
      throw new Error('fetchTaskBarrier must be set when entering fetchFromTD.');
    /** @type {*} */ // Supress red-squigglies on clearTimeout.
    let fetchTimeout = dcp_timers.setTimeout(() => {
      fetchTimeout = null;
      this.fetchTaskBarrier = false;
      this.warning('Fetch exceeded timeout, will reconnect at next watchdog interval');
    }, 3 * 60 * 1000);  // Max out at 3 minutes to fetch.
    // Allow workers and localExec to exit.
    fetchTimeout.unref();

    const finalize = () => {
      this.fetchTaskBarrier = false;
      if (fetchTimeout)
        dcp_timers.clearTimeout(fetchTimeout);
      fetchTimeout = null;
    }

    // Ensure result submitter and task distributor connections before fetching tasks.
    try
    {
      await Promise.all([
        this.dcp4.taskDistributor.keepalive(),
        this.dcp4.resultSubmitter.keepalive(),
      ]);
    }
    catch (error)
    {
      selectiveSupEx() && console.debug('fetchFromTD: Keepalive failed for either result-submitter or task-distributor', error);
      // Don't display 'EPIPE','ECONNRESET','ECONNREFUSED','ECONNABORTED','ETIMEDOUT','ESANDBOX', errors.
      if (!Supervisor.isReportable(error))
        this.warning('Failed to connect to result submitter or task-distributor.', 'Will try again at next fetch cycle.');
      return finalize();
    }

    // Create the request.
    const requestPayloadData = this.createTDRequest(cpuCoreSpace);
    selectiveDebug2() && console.debug(`FetchFromTD(${Date.now()-this.lastTime}): Before sending message(${this.dcp4.taskDistributor?.id})`, this.fetchState.valueOf(), requestPayloadData);

    if (!requestPayloadData)
    {
      // Can happen when (!normal && notifySoteria) is true, before the call to createTDRequest,
      // but nothing was found for Soteria to do. This occurs when sandboxes cannot be created,
      // but otherwise it should be rare.
      selectiveDebug() && console.warn(`Leaving fetchTask since there's no need to send a request to TD: ${this.fetchState.valueOf()}, ${this.isReadyForWork}`);
      return finalize();
    }
    assert(this.fetchState.normal || this.fetchState.notifySoteria || this.fetchState.startupCheck);
    assert(requestPayloadData && FetchState.check(requestPayloadData.fetchState));

    // The 'beforeFetch' event allows the user to cancel the requestTask payload.
    let canceled = false;
    /**
      * The 'beforeFetch' event fires before the request is sent to requestTask in task distributor.
      * @link https://gitlab.com/Distributed-Compute-Protocol/dcp-docs-wes/-/blob/wip/worker/worker-events.md
      * @event Supervisor#beforeFetch
      */
    this.worker.emit('beforeFetch', () => { canceled = true; })
    if (canceled)
    {
      this.warning('Leaving fetchTask: beforeFetch cancel function was called');
      return finalize();
    }

    if (dcpConfig.worker?.requestSnapshot && !DCP_ENV.isBrowserPlatform)
    {
      const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
      const fs = requireNative('fs');
      const os = requireNative('os');
      const path = requireNative('path');
      const snapshot = {
        date:         new Date(),
        request:      requestPayloadData, /* naming needs addressing. /wg Mar 2025 */
        identity:     (await wallet.getId()).address,
        workerConfig: this.options.options,
        workerState:  this.workerState,
        modules:      Object.keys(this.moduleCache.cache),
        discreteJobs: this.discreteJobs.buf,
        rejectedJobs: this.rejectedJobs.buf,
        allowOrigins: {},
        jobs:         this.jobManagerInventory.map(item => {
          const msg = Object.assign({}, item.jobMessage);
          delete msg.workFunction;
        }),
        system: {
          runDuration: performance.now(),
          nodeVersion: process.version,
          arch:        process.arch,
          platform:    process.platform,
          cpuCount:    os.cpus().length,
          cpuModel:    os.cpus()[0].model,
          loadavg:     os.loadavg(),
          totalmem:    os.totalmem(),
          freemem:     os.freemem(),
        },
      }
      for (let accessMethod in this.originManager.origins)
        snapshot.allowOrigins[accessMethod] = this.originManager.getAllowList('fetchData', null);

      const serializer = dcpConfig.worker.requestSnapshot.endsWith('.kvin') ? requireNative('KVIN').KVIN : JSON;
      fs.writeFileSync(path.resolve(path.dirname(requireNative.main.filename), dcpConfig.worker.requestSnapshot),
                       serializer.stringify(snapshot), 'utf-8');
    }

    // It's possible that a call to stopWork could slip in right after the above keepalives,
    // and hence this.dcp4.taskDistributor could be closed, so check whether Supervisor is ready.
    // Or pause was just called.
    if (!this.isReadyForWork)
      return finalize();

    const tdRequest = new this.dcp4.taskDistributor.connection.Request({ operation: 'requestTask@3', data: requestPayloadData });
    tdRequest.connection.on('beforeSend', function beforeSendHandler (request) {
      /* need to delay hashing because connection might not be established yet, but we need the dcpsid */
      if (request !== tdRequest)
        return;
      const hashedComputeGroups = generateTaskRequestWorkerComputeGroups(that.worker.config, request);
      request.payload.data.workerComputeGroups = hashedComputeGroups;
      tdRequest.connection.off('beforeSend', beforeSendHandler);
    });

    selectiveDebug2() && console.debug('fetchFromTD: sending request to TD', this.isReady, this.evaluator.reallyDown, Date.now() - this.lastTime);
    return tdRequest.send()
      .then(async (response) => {
        assert(this.fetchState.valueOf() === requestPayloadData?.fetchState);

        // Check for success and don't proceed when failed.
        // Undefined response.payload can happen rarely.
        if (!response.success || !response.payload)
        {
          selectiveDebug() && console.debug('Task fetch failure; request=', requestPayloadData);
          selectiveSupEx() && console.debug('Unable to request task from scheduler; will try again on a new connection', response.success, response.payload);
          this.warning('Unable to request task from scheduler; will try again on a new connection');
          return this.roundRobinSlices();
        }

        // This should not fire, however the old op-router would sometimes return an ErrorPayload
        // when response.success=true.
        if (selectiveSupEx() && response.success
          && (response.payload instanceof this.dcp4.taskDistributor.connection.ErrorPayload))
          throw Error('Received ErrorPayload from task distributor with response.success=true');

        // Record Soteria notify time stamp.
        if (FetchState.notifySoteria(requestPayloadData.fetchState))
          this.lastSoteriaRequest = Date.now();

        // Check for pending notifications,
        if (response.payload?.body?.pendingNotifications)
          await this.handleNotifications(response.peerAddress);

        // There's a race where while shutting down for stopWork or evaluatorEnd, there could be a
        // TD request in-flight. For this race we do need to sendReturnAllSlicesMessage.
        // We return here because the fetch call was a race. There's
        // no reason to record it in worker management because we're returning the slices immediately.
        if (this.state.in([ 'stopping', 'stopped' ]) || this.evaluator.reallyDown)
          return this.sendReturnAllSlicesMessage();

        // Add information about the latest task fetch to the database.
        management.storeTaskInfo(this.worker, response);

        // Process task fetched from TD.
        if (FetchState.normal(requestPayloadData.fetchState))
          return addTaskToWorkload(requestPayloadData, response); // Process the task.

        // Call this.roundRobinSlices if there's nothing else to do.
        assert(FetchState.notifySoteria(requestPayloadData.fetchState));
        if (FetchState.roundRobin(requestPayloadData.fetchState))
          return this.roundRobinSlices();

        return response;
      })
      .catch((error) => {
        this.worker.emit('fetch', error);
        selectiveSupEx() && Supervisor.isReportable(error) && console.debug('Unable to request task from scheduler. Will try again on a new connection.', error);
        //throw error; /* SAVE -- Enable when we want to force a crash right after fetching a task. */
      })
      .finally(() => {
        return finalize();
      });
  }

  /**
   * Remove all unreferenced jobs in this.jobManagerInventory.
   * Since job-managers are inserted into this.jobManagerInventory with a push, the job managers at the beginning are oldest.
   * @param {Object<string, number[]>} [newJobMap] - Jobs that should not be removed from this.jobManagerInventory.
   */
  clearUnusedJobs (newJobMap)
  {
    for (const jobMan of this.jobManagerInventory) // Grab oldest 1st
    {
      if (!newJobMap || !newJobMap[jobMan.jobId])
      {
        let isEmpty = true;
        for (const slice of jobMan.sliceInventory)
          if (slice.isQueuedOrActive)
          {
            isEmpty = false;
            break;
          }
        if (isEmpty)
          this.purgeJob(jobMan, 'Removing empty job');
        // Do we ever want to try to save some empty jobs that have assigned sandboxes?
      }
    }
  }

  // _Idx
  //
  // createSandbox, returnSandbox, hookUpSandboxListeners, pruneSandboxes
  //

  /**
   * Try to create a sandbox while waiting up to sandboxCreationTimeoutMs, otherwise exit.
   * Ideally sandbox.start() would throw when evaluator sandbox creation is throttled.
   * @todo It could be better for this timeout to be in sandbox.start().
   *
   * Create a READY sandbox within timeoutMs or return null.
   * @param {boolean} [throwError=false]
   * @returns {Promise<Sandbox>}
   */
  async createSandboxWithTimeout (throwError = false)
  {
    const sandbox = new Sandbox(this, { ...this.options.sandboxOptions });
    selectiveDebug2() && console.debug(`createSandboxWithTimeout(${sandbox.id}): Calling sandbox.start: ${this.evaluator.createSandboxRefCount}, really-down ${this.evaluator.reallyDown}`);

    const that = this;
    return new Promise(async function supervisor$createSandboxPromise(resolve, reject) {
      /** @type {*} */ // Supress red-squigglies on clearTimeout.
      const createSandboxTimeout = dcp_timers.setTimeout(function supervisor$createSandbox$fail() {
        selectiveSupEx() && console.debug(`createSandboxWithTimeout(${that.workerId},${new Date()}): Unable to create sandbox within ${that.options.sandboxCreationTimeoutMs * common.timeDilation * 1e-3}s, will try again...`);
        sandbox?.terminate(false);
        if (throwError)
          reject(new Error(`Unable to create sandbox within ${that.options.sandboxCreationTimeoutMs * common.timeDilation}ms, will try again...`));
        resolve(null);
      }, that.options.sandboxCreationTimeoutMs * common.timeDilation);
      createSandboxTimeout.unref();

      try
      {
        // Try to create a sandbox and clear the above timeout.
        await sandbox.start();
        dcp_timers.clearTimeout(createSandboxTimeout);
        that.hookUpSandboxListeners(sandbox);

        if (that.evaluator.reallyDown)
        {
          that.evaluator.reallyDown = false;
          that.sandboxBackoffManager.reset();
          // Reinitialize capabilities after evaluator restarts.
          await that.checkCapabilities(sandbox);
        }
        else if (!that.capabilities)
        {
          await that.checkCapabilities(sandbox);
        }
        assert(that.capabilities);
        that.evaluator.downInterlock = false; // Should be redundant

        selectiveDebug() && console.debug(`createSandboxWithTimeout: Created sandbox ${sandbox.identifier} for worker ${that.workerId}`, sandbox?.capabilities);

        resolve(sandbox);
      }
      catch(error)
      {
        dcp_timers.clearTimeout(createSandboxTimeout);
        selectiveDebug() && console.debug(`createSandboxWithTimeout: Failed to create sandbox${sandbox.id}`, that.capabilities, error.message, error.code);

        sandbox?.terminate(false);
        if (throwError)
          reject(error);
        resolve(null);
      }
    });
  }

  /**
   * Automatically handle when a sandbox cannot be created due to evaluator death or throttling.
   *
   * An evaluator may be stopped and started again with sa-worker running, and have good behavior.
   * All evaluators may be stopped and started again with sa-worker running, and have good behavior.
   *
   * @returns {Promise<Sandbox>}
   */
  async spinCreateSandbox ()
  {
    // If the evaluator cannot start (e.g. if the evalServer is not running),
    // then the retry-wait-loop will keep retrying until the evalServer comes online.
    try
    {
      this.evaluator.createSandboxRefCount++;

      let retry = 0;
      while (this.isReady)
      {
        try
        {
          selectiveDebug2() && console.debug(`spinCreateSandbox(retry=${retry}) creating sandbox`, this.state.valueOf(), this.sandboxInventory.map((sbx) => sbx.identifier));
          const sandbox = await this.createSandboxWithTimeout(true /*throwError*/);
          if (sandbox)
          {
            // Place READY sandboxes at the beginning and ASSIGNED at the end.
            this.sandboxInventory.unshift(sandbox);
            return sandbox;
          }
        }
        catch (error)
        {
          selectiveDebug2() && console.debug(`Supervisor.spinCreateSandbox: Failed to start sandbox`, this.state.valueOf(), this.evaluator.createSandboxRefCount, this.evaluator.reallyDown, retry, error.message, error.code);
          if (DCP_ENV.isBrowserPlatform)
            throw error; /* no retry-recoverable errors when Worker() can't instantiate */

          // Retry on non-reportable errors.
          if (!Supervisor.isReportable(error))
          {
            const retryLimit = this.worker.config.evaluator?.retryLimit ?? 30;
            const retrySleep = this.worker.config.evaluator?.retrySleep ?? 8;

            // The evaluator may be down or shutting down, keep retrying.
            if ((retry++ % retryLimit) === 0)
            {
              if (common.highPriorityDiag(/*displayOnDebugBuild*/ true))
                this.warning(`Failed to start a sandbox; will keep retrying (#attempts: ${retry})...evaluator may be throttled or terminated`);
              else
                this.warning(`Failed to start a sandbox; will keep retrying...evaluator may be throttled or terminated`);
            }

            const maxSleep = Math.min(retry, retrySleep);
            for (let k = 0; k < maxSleep; k++)
              if (this.isReady)
                await utils.a$sleep(1 + Math.random() * 0.5);
          }
          else
          {
            // Explicitly list some of the errors we want to throw.
            switch(error.code)
            {
              case 'ENOTFOUND':     /* DNS failure - could not look up evaluator host? */
              case undefined:
              default:
                throw error;
            }
          }
        }
      }
    }
    finally
    {
      this.evaluator.createSandboxRefCount--;
    }
  }

  /**
   * Remove sandbox from inventory and terminate.
   * @param {Sandbox} sandbox
   * @param {Boolean} [rejectWorkingSandbox] - True when we want to kick a working sandbox out of execution.
   */
  returnSandbox (sandbox, rejectWorkingSandbox = false)
  {
    selectiveDebug2() && console.debug(`returnSandbox: ${sandbox.identifier}`);

    // If sandbox is not in this.sandboxInventory then sandbox must already be terminated or
    // termination has been delayed.
    const index = this.sandboxInventory.indexOf(sandbox);
    const delayedTermination = !sandbox.terminate(rejectWorkingSandbox && sandbox.isWorking);
    const shouldNotHappen = (index === -1 && !sandbox.isTerminated && !delayedTermination);

    if (shouldNotHappen)
    {
      selectiveSupEx() && console.debug(`returnSandbox: Non-terminated sandbox ${sandbox.identifier} has already been removed.`, index, sandbox.isTerminated, delayedTermination, this.sandboxInventory.map((sbx) => sbx.identifier));
      if (common.highPriorityDiag(/*displayOnDebugBuild*/ true))
        throw new Error(`returnSandbox: Non-terminated sandbox ${sandbox.identifier} has already been removed.`);
      this.warning(`returnSandbox: Non-terminated sandbox ${sandbox.identifier} has already been removed.`);
    }
  }

  /**
   * For a given sandbox, hook up all the Sandbox listeners.
   * @param {Sandbox} sandbox
   */
  hookUpSandboxListeners (sandbox)
  {
    sandbox.addListener('start', () => {
      if (!(sandbox.slice && this.options.sendStatusBeginMessage))
        return;
      const payload = {
        worker: this.workerId,
        slices: [ sandbox.slice.getMessage('begin') ],
      };
      return this.dcp4.safeRSStatus(payload, `Failed to send 'begin' status for slice ${sandbox.slice.identifier}`);
    });

    const that = this;
    // Sandbox internal error handler.
    sandbox.on('sandboxError', function Supervisor$sandboxError(error) {
      selectiveSupEx() && console.debug(`Sandbox ${sandbox.identifier} sandboxError-handler; error while executing work function`, error);
      const slice = sandbox.slice;
      if (!slice?.isWorking) // Sanity -- warning should never fire.
        this.warning(`handleSandboxError: slice ${slice?.identifier} must be WORKING.`);
      that.returnSandbox(sandbox);
      if (slice) // Should never be null.
        that.returnSlice(slice, 'sandboxErrorfired');
    });

    // Sandbox complete handler.
    // When any sandbox completes, go through the Supervisor.fetchTask protocol.
    sandbox.addListener('complete', () => {
      // Try not to call fetchTask unless there's something there.
      if (!this.fetchTaskBarrier)
      {
        selectiveDebug2() && console.debug('Sandbox complete listener', this.fetchTaskBarrier, this.roundRobinBarrier, this.unusedSandboxCount, Date.now() - this.lastTime);
        this.fetchTask().catch ((error) => {
          selectiveSupEx() && console.debug('Complete event fetchTask failed:', error);
        });
      }
    });
  }

  /**
   * If evaluatorHandle 'end' event fires for a sandbox, it could be OOM or something else that
   * the evaluator can't handle or it could be some kind of throttling. So we attempt to recover
   * sandbox.slice when appropriate. If evaluatorHandle 'end' event fires for all sandboxes, then
   * all evaluators could be down, so we try to create a new sandbox and if that fails, we return
   * all slices to the scheduler, except we try to submit results for those that have completed work.
   * Called each time a sandboxes evaluatorHandle 'end' event fires.
   *
   * To implement this right we need DCP-CLIENT StandaloneWorker to emit a throttling event each
   * time an evaluator sandbox is prevented from being created. Then for each throttling event we
   * decrement this.maxSandboxes and possibly this.cores.cpu (...hardware.vCores ?) Logic similar
   * to reproMan takes away some of the slice inventory too.
   *
   * However, currently we only know for sure when all evaluators are down.
   * @param {Sandbox} sandbox
   */
  async evaluatorEnd (sandbox)
  {
    selectiveDebug2() && console.debug(`evaluatorEnd(${this.evaluator.downInterlock},${this.evaluator.reallyDown}): ${sandbox.identifier}, #non-terminated-sandboxes ${this.sandboxInventory.filter((sbx) => !sbx.isTerminated).map((sbx) => sbx.identifier)}`);
    // We know that sandbox.slice is not going to execute on sandbox.
    // Try to recover slice here: RESERVED --> READY or WORKING --> READY.
    if (sandbox.slice)
      sandbox.slice.tryRecoverSlice(); // Should check to see whether slice has been removed from inventory -- meaning slice has been returned or has sent result to RS

    // Does there exist a non-terminated sandbox?
    for (const sbx of this.sandboxInventory)
      if (!sbx.isTerminated)
        return;

    if (!(this.evaluator.downInterlock || this.evaluator.reallyDown))
    {
      // When we get here, all sandboxes have been terminated.
      // Set downInterlock to prevent interleaving when handling multiple evaluatorHandle 'end' events.
      // Note that it is possible to get here for more than one evaluatorHandle 'end' event,
      //  where the second one enters after the first one exists.
      try
      {
        this.evaluator.downInterlock = true;
        // Check if it is possible to create a new sandbox.
        selectiveDebug() && console.debug('evaluatorEnd: Try to create a new sandbox', sandbox.identifier, this.sandboxInventory.map((sbx) => sbx.identifier));
        const sbx = await this.createSandboxWithTimeout(true /*throwError*/);
        // Place READY sandboxes at the beginning and ASSIGNED at the end.
        this.sandboxInventory.unshift(sbx);
        // Successfully creating a sandbox calls this.evaluator.reset()...
        selectiveDebug() && console.debug('evaluatorEnd: Successfully created new sandbox', sbx.identifier);
      }
      catch (error)
      {
        // Since all sandboxes have been terminated, if we cannot create a new sandbox, that means
        // no evaluator is running (e.g. screensaver could be down). Try to submit results for
        // completed slices, but return all other non-finished slices to the scheduler.
        selectiveSupEx() && console.debug(`evaluatorEnd: Cannot create new sandbox (${error.code || 'evaluator not listening?'})`, this.evaluator, this.sandboxInventory.map((sbx) => sbx.identifier), error.code!=='ESANDBOX'?error:{});
        this.evaluator.reallyDown = true;
        this.emit('evalDown');
        this.destroyAllJobs(/*evaluatorDown*/ true);
        if (common.highPriorityDiag(/*displayOnDebugBuild*/ true)) // Need to see warning when testing.
          return this.warning(`${new Date()}: Stopping all work\nEvaluator cannot create new sandbox`);
        this.warning('Stopping all work', 'Evaluator cannot create new sandbox');
      }
      finally
      {
        this.sandboxInventory = this.sandboxInventory.filter(sbx => !sbx.isTerminated);
        this.evaluator.downInterlock = false;
      }
    }
  }

  /**
   * Terminate extra sandboxes.
   * The most common case will be this.sandboxInventory.length == maxSandboxes and count == 1
   *
   * When in a private compute group, there will be fewer jobs so it's likely that a given job will be seen again.
   * On the other hand, TD mostly prioritises jobs with largest DCC/second so those jobs could also be seen again.
   * @todo XXXpfr Prioritize keeping expensive to assign sandboxes.
   *
   * Don't purge jobs here: can accidentally purge a job that TD just fetched (XXXpfr)
   *
   * @param {number} count - prune enough to create count new sandboxes w/o going over maxSandboxes.
   */
  pruneSandboxes (count)
  {
    if (!this.isReadyForWork)
      return;
    assert(count >= 0);

    //
    //  THE PRUNING PLAN:
    //
    // 0) At any step, when pruneCount < 1, we're done
    // 1) Prune all READY sandboxes
    // 2) Shuffle jobs to help assure fairness
    // 3) Prune 1 ASSIGNED sandbox from every job where job.assignedSandboxes.length > 2
    // 4) Segregate jobs that have no unstarted work and immediately prune 1 ASSIGNED sandbox from no work jobs
    // 4) For no work jobs: prune all jobs with assignedSandboxes.length > 2, then with length > 1, then the rest
    // 5) For jobs with work: prune all jobs with assignedSandboxes.length > 2, then with length > 1, then the rest

    // Compute pruneCount.
    let pruneCount = count + this.sandboxInventory.length - this.options.maxSandboxes;
    if (pruneCount < 1)
      return;

    // It should be rare that we find a terminated sandbox.
    let foundTerminated = false;
    for (const sbx of this.sandboxInventory)
      if (sbx.isTerminated)
      {
        foundTerminated = true;
        break;
      }
    if (foundTerminated)
    {
      this.sandboxInventory = this.sandboxInventory.filter((sandbox) => !sandbox.isTerminated);
      // re-compute
      pruneCount = count + this.sandboxInventory.length - this.options.maxSandboxes;
      if (pruneCount < 1)
        return;
    }

    selectiveDebug() && console.debug(`Supervisor.pruneSandboxes START: pruneCount ${pruneCount}/${this.sandboxInventory.length}/${this.options.maxSandboxes}.`);

    const pruneOneSandbox = (sandboxes, debugMsg) => {
      assert(sandboxes.length > 0);
      this.returnSandbox(sandboxes[0]);
      if (--pruneCount < 1)
      {
        selectiveSupEx() && console.debug(`${debugMsg}: ${pruneCount}/${this.sandboxInventory.length}/${this.options.maxSandboxes}.`);
        return true;
      }
      return false;
    }

    // Prune ready sandboxes first.
    while (this.sandboxInventory[0]?.isReady)
    {
      selectiveDebug2() && console.debug('pruneSandboxes(ready):', pruneCount, Date.now() - this.lastTime, this.sandboxInventory.map((sbx) => sbx.identifier));
      if (pruneOneSandbox(this.sandboxInventory, 'prune READY sandbox'))
        return;
    }

    // Make shallow copy so we can shuffle jobs in order to help assure fairness.
    const jobManagers = [...this.jobManagerInventory];
    utils.shuffle(jobManagers);

    // Do 1 round robin pruning pass when assignedSandboxes.length > 2.
    // Jobs that roughly have no work left to do are prioritized to prune.
    const jobSandboxes = [], emptyJobSandboxes = [];
    for (const jm of jobManagers)
    {
      const assignedSandboxes = jm.assignedSandboxes;
      if (assignedSandboxes.length > 0)
      {
        // Prune 1 sandbox when there are more than 2
        if (assignedSandboxes.length > 2)
        {
          if (pruneOneSandbox(assignedSandboxes, 'prune ASSIGNED sandbox when #sandboxes > 2'))
            return;
        }

        // Segregate jobs that don't have any unstarted sandbox work left to do and prune those 1st.
        if (jm.sliceInventory.length === 0)
        {
          // Do the easiest one.
          if (pruneOneSandbox(assignedSandboxes, 'prune ASSIGNED sandbox from EMPTY jobs:1'))
            return;
          if (assignedSandboxes.length > 0)
            emptyJobSandboxes.push(assignedSandboxes);
        }
        else
        {
          // Check for no unstarted work the hard way.
          let hasWork = false;
          for (const slice of jm.sliceInventory)
            if (slice.isQueued || slice.isReserved)
            {
              hasWork = true;
              break;
            }
          if (hasWork)
            jobSandboxes.push(assignedSandboxes);
          else if (assignedSandboxes.length > 0)
          {
            if (pruneOneSandbox(assignedSandboxes, 'prune ASSIGNED sandbox from EMPTY jobs:2'))
              return;
            if (assignedSandboxes.length > 0)
              emptyJobSandboxes.push(assignedSandboxes);
          }
        }
      }
    }

    const pruneByLength = (_jobSandboxes, debugMsg) => {
      if (_jobSandboxes.length > 0)
      {
        // Prune the sandboxes from all jobs with no unstarted work.
        // Try to keep approximately the same # of assigned sandboxes per job.
        for (let k = 2; k >= 0; k--)
        {
          // Do round robin pruning until assignedSandboxes.length <= k
          while (true)
          {
            let _pruneCount = pruneCount;
            for (const assignedSandboxes of _jobSandboxes)
            {
              if (assignedSandboxes.length > k)
              {
                selectiveDebug2() && console.debug(`prune sandboxes ordered by assignedSandboxes.length: ${assignedSandboxes.length}, pruneCount: ${pruneCount}`, this.sandboxInventory.map((sbx) => sbx.identifier));
                if (pruneOneSandbox(assignedSandboxes, debugMsg))
                  return true;
              }
            }
            if (_pruneCount === pruneCount)
              break;
          }
        }
      }
      return false;
    }

    if (pruneByLength(emptyJobSandboxes, 'prune ASSIGNED sandbox from EMPTY jobs:3'))
      return;
    if (pruneByLength(jobSandboxes, 'prune ASSIGNED sandbox from NON-EMPTY jobs'))
      return;

    // Should never get here;
    if (this.sandboxInventory.length < this.options.maxSandboxes)
      return;
    this.warning('Unable to prune sandboxes down to the desired level.');
  }

  // _Idx
  //
  // recordResult, sendToResultSubmitter, sendResultToRemote
  //

  /**
   * Submits the slice results to the result-submitter service.
   * Then remove the slice from the its job manager.
   *
   * @param {Slice}         slice - The slice to submit.
   * @param {SandboxHandle} sandboxHandle - The sandbox handle associated to the slice.
   * @returns {Promise<any>}
   */
  async recordResult (slice, sandboxHandle)
  {
    if (!slice.isComplete)
      throw new DCPError(`Cannot record result for slice ${slice.identifier} that has not completed execution successfully.`);
    if (!slice.timeReport)
      throw new DCPError(`Invalid time report for slice ${slice.identifier} in recordResult`);
    if (!slice.dataReport)
      throw new DCPError(`Invalid data report for slice ${slice.identifier} in recordResult`);

    selectiveDebug2() && console.debug(`Supervisor: recording result for slice ${slice.identifier} with metrics`, slice.metrics);

    /** @see result-submitter::result for full message details */
    const payloadData = {
      slice:          slice.sliceNumber,
      job:            slice.jobId,
      worker:         this.workerId,
      paymentAddress: this.options.paymentAddress,
      metrics:        slice.metrics,
      authorizationMessage: slice.authorizationMessage,
    };

    let canceled = false;
    const resultUrl = slice.resultStorageDetails?.length ? slice.resultStorageDetails : false;
    this.worker.emit(   'beforeResult', () => { canceled = true; }, resultUrl, slice.jobId, slice.sliceNumber);
    this.jobEmit(slice, 'beforeResult', () => { canceled = true; }, resultUrl, slice.sliceNumber);
    selectiveDebug() && canceled && console.debug(`User canceled the result submission operation for slice ${slice.identifier}.`);
    if (canceled)
      return this.returnSlice(slice, 'Cancelled via beforeResult event');

    if (slice.resultStorageType === 'pattern')
      return this.sendResultToRemote(slice)
        .then((response) => {
          // Remote results have a small response, containing a URL of the actual result location.
          payloadData.result = utils.encodeDataURI(response);
          return this.sendToResultSubmitter(slice, sandboxHandle, payloadData);
        });

    payloadData.resultV2 = kvin.marshal(serializeJobValue(slice.result.result, slice.jobManager.serializer));
    return this.sendToResultSubmitter(slice, sandboxHandle, payloadData);
  }

  /**
   * Send result to result submitter.
   * @param {Slice}  slice
   * @param {SandboxHandle} sandboxHandle
   * @param {*}      payloadData
   * @returns {Promise<any>}
   */
  async sendToResultSubmitter (slice, sandboxHandle, payloadData)
  {
    // When a given slice hits the retry limit (6 retries) the slice is returned to scheduler.
    const handleRSError = (error, slice) => { // eslint-disable-line no-shadow
      const msg = `Failed to submit results to scheduler for slice ${slice.identifier}`;
      if (!error)
        error = new Error(msg);
      selectiveSupEx() && console.debug(msg, error);

      // Exclude 'EPIPE','ECONNRESET','ECONNREFUSED','ECONNABORTED','ETIMEDOUT','ESANDBOX' errors.
      if (Supervisor.isReportable(error))
      {
        // Please fix "DCP-4732 error recovery in recordResults/handleRSError is not right"
        /** @todo HI-PRI Make RS retryable similar to fetchThing in JobManager, but only for certain errors (PFR July 2025) */
        /** @todo HI-PRI Figure out which errors should be retryable (PFR July 2025) */
        this.directError(error, msg);
        slice['retrySubmitResults'] = (slice['retrySubmitResults'] ?? 0) + 1;
        // Allow retries w/o error?
        if (this.state.is('stopping') // DCP-4733 Emit 'result' error event when 'stopping'
          || slice['retrySubmitResults'] > this.options.maxSliceRetries)
        {
          this.handleFailedSlice(slice, error);
          this.worker.emit(   'result', error, undefined, slice.jobId, slice.sliceNumber);
          this.jobEmit(slice, 'result', error, slice.sliceNumber);
          this.returnSlice(slice, common.errorCodeOrString(error, `sendToResultSubmitter: ${error.message}`));
          throw error;
        }
      }
    }

    try
    {
      await this.delayManager.nextDelay('recordResult', 2);
      // ->console.log('recordResult', slice.identifier, Date.now() - this.lastTime); // SAVE pfr

      selectiveDebug2() && console.debug(`Supervisor.sendToResultSubmitter: Before sending message(${this.dcp4.resultSubmitter?.id})`, slice.identifier);
      this.worker.emit('metrics', { jobId: slice.jobId, sliceNumber: slice.sliceNumber, metrics: slice.metrics, status: 'result' });

      return this.dcp4.resultSubmitter.request('result', payloadData)
        .then((resp) => {
          const payload = resp.payload;
          if (!resp.success)
          {
            selectiveSupEx() && console.debug(`resultSubmitter.send failed for slice ${slice.identifier}`, payload ? utils.stringify(payload) : 'NO PAYLOAD');
            if (payload)
              throw payload;
            throw new Error(`Result submitter failed with no payload when recording results for ${slice.identifier}.`);
          }

          selectiveDebug() && console.debug(`Successfully submitted results for slice ${slice.identifier} and worker ${this.workerId}: CPU=${payload.metrics.sliceCPUTime}/${payload.metrics.sliceCPUDensity}, GPU=${payload.metrics.sliceGPUTime}/${payload.metrics.sliceGPUDensity}`, Date.now()-this.lastTime);

          slice.jobManager.update({ metrics: payload.metrics }); // Update metrics

          slice.markAsFinished();

          // Remove the slice from the job manager.
          slice.jobManager.removeSlice(slice);

          // Emit the 3 'payment' events.
          const paymentAddress = payloadData.paymentAddress.toString();
          this.worker.emit(   'payment', payload.slicePaymentAmount, paymentAddress, slice.jobId, slice.sliceNumber);
          this.jobEmit(slice, 'payment', payload.slicePaymentAmount, paymentAddress, slice.sliceNumber);
          // Need sliceNumber because sandbox may have already been reused for another slice.
          sandboxHandle.emit( 'payment', payload.slicePaymentAmount, paymentAddress, slice.sliceNumber);
          this.unrecordedEarnings += parseFloat(payload.slicePaymentAmount);

          // Emit the 2 'result' events.
          const payloadLength = kvin.stringify(payloadData).length; /** @todo - fix per DCP-3750 */
          const resultUrl = slice.resultStorageDetails?.length ? slice.resultStorageDetails : false;
          this.worker.emit(   'result', resultUrl, payloadLength, slice.jobId, slice.sliceNumber);
          this.jobEmit(slice, 'result', resultUrl, payloadLength, slice.sliceNumber);

          // (Move this after the result event.)
          // First running slice often waits 7 seconds until fetchTask() -- detect it and call fetchTask().
          // There is often a delay before executing the 1st slice after a job becomes 'running'.
          // So fire up fetchTask after sending the result of any estimation slice.
          // Ideally we'd do this only on the last estimation slice, but that's difficult to detect.
          if (slice.isEstimation && !this.fetchTaskBarrier)
          {
            selectiveDebug2() && console.debug('RS fetchTask', Date.now() - this.lastTime);
            this.fetchTask().catch (async (error) => {
              // fetchTask is allowed to fail w/o hurting the worker.
              selectiveSupEx() && console.debug('RS fetchTask failed:', error);
            });
          }

          if (this.sliceTiming)
          {
            slice['resultDelta'] = Date.now() - slice['resultDelta'];
            console.debug(`recordResult(${slice['executionDelta']}, ${slice['resultDelta']}): Completed slice ${slice.identifier}.`, Date.now() - this.lastTime);
          }

          return resp;
        })
        .catch ((error) => {
          handleRSError (error, slice);
        });
    }
    catch (error)
    {
      handleRSError (error, slice);
    }
  }

  /**
   * Send a work function's result to a server that speaks our DCP Remote Data Server protocol.
   * E.g. https://gitlab.com/Distributed-Compute-Protocol/dcp-rds
   *
   * @param {Slice} slice - Slice object whose result we are sending.
   * @returns {Promise<string>}
   * @throws When HTTP status not in the 2xx range.
   */
  async sendResultToRemote (slice)
  {
    /** XXXpfr @todo: Support file upload. */
    function serializeContent(result, category = { isJSON: true, isKVIN: false, isBinary: false, isText: false }) {
      if (category.isJSON) return JSON.stringify(result);
      if (category.isKVIN) return kvin.serialize(result);
      if (category.isBinary) return new Uint8Array(result.buffer);
      return result;
    }

    // Construct postParams.
    const postParams = { ...slice.resultStorageParams };
    if (!postParams.contentType)
      postParams.contentType = 'application/json';
    if (!postParams.element)
      postParams.element = slice.sliceNumber;
    debugging('supervisor') && console.debug('sendResultToRemote postParams:', postParams);

    // Categorize postParams.contentType.
    const category = utils.categorizeContentType(postParams.contentType);

    // Construct result.
    const result = slice.result.result;
    if (result)
      postParams.content = serializeContent(result, category);
    else
      postParams.error = serializeContent(slice.error); /** XXXpfr @todo This isn't correct; slice.error is never set. */
    debugging('supervisor') && console.debug('sendResultToRemote content:', (result ? postParams.content : postParams.error).slice(0, 128));

    // Construct url.
    const sliceResultUri = slice.resultStorageDetails;
    // cf.DCP-3679
    // const sliceResultUri = makeValueURI('pattern', slice.resultStorageDetails, {
    //   slice: slice.sliceNumber,
    //   job:   slice.jobId,
    // });
    debugging() && console.debug('sendResultToRemote sliceResultUri:', sliceResultUri);
    const url = new DcpURL(sliceResultUri);

    // Check allowed origins.
    if (!this.originManager.isAllowed(url.origin, 'sendResults', slice.jobManager.public.computeGroups[0]?.opaqueId))
      throw new DCPError(`Invalid origin for remote result storage: '${url.origin}'`, 'EPERM_ORIGIN');

    let body = postParams, type = 'string';
    /**
     * @todo XXXpfr Need to properly implement FormData to support multipart/form-data POST
     * (cf. DCP branch improvement/dcp-3446-multipart-remote-results)
     *
     */
    // if (category.isBinary)
    // {
    //   // Set up a multipart/form-data POST in order to upload binary data.
    //   const entries = Object.entries(postParams);
    //   const form = new FormData();

    //   entries.forEach((kvp) => {
    //     if (ArrayBuffer.isView(kvp[1]))
    //     {
    //       if (!DCP_ENV.isBrowserPlatform)
    //         form.append(kvp[0].toString(), new Uint8Array(kvp[1].buffer));
    //       else
    //         form.append(kvp[0].toString(), new Blob([new Uint8Array(kvp[1].buffer)]));
    //     }
    //     else
    //       form.append(kvp[0].toString(), kvp[1].toString());
    //   });

    //   if (!DCP_ENV.isBrowserPlatform)
    //   {
    //     body = form.getBuffer();
    //     type = form.getHeaders()['content-type'];
    //   }
    //   else
    //   {
    //     body = form;
    //     type = 'multipart/form-data';
    //   }
    // }

    // Fetch.
    return justFetch(url, type, 'POST', false, body);
  }

  // _Idx
  //
  // handleWorkReject
  //

  /**
   * Handles reassigning or returning a slice that rejected.
   * Syntax: work.reject(stringReason, retries = 0)
   *   - Old work.reject(stringReason) is equivalent to work.reject(stringReason, 0)
   *   - Old work.reject(false) is equivalent to work.reject(false, 1)
   *
   * slice.rejectedState must have already been constructed with:
   *     reason  = slice.rejectedState.info.message
   *     retries = slice.rejectedState.info.retries
   *     retry   = slice.rejectedState.retry
   *
   * retries: number of times the slice will be retried before the job is rejected.
   * retry:   number of times the slice has been retried.
   *
   * If retry < retries, reschedule the slice and increment retry++.
   * If retry ==== retries, then return all slices from the job to the scheduler, reject the job,
   * and terminate all sandboxes with that jobId.
   *
   * @param {Slice} slice
   * @returns {boolean} - True when job has been purged
   */
  handleWorkReject (slice)
  {
    const rejectedState = slice.rejectedState;
    assert(rejectedState);
    const { message: reason, retries } = rejectedState.info;
    const retry = rejectedState.retry;
    const jobManager = slice.jobManager;
    if (jobManager.dead)
       return; // Job has been already purged.

    debugging() && console.debug(`handleWorkReject:, reason=${reason}, retries=${retries}, retry=${retry}`, slice.identifier, rejectedState.info);

    jobManager.rejectedJobReasons.push({ slice: slice.sliceNumber, reason, retries }); // memoize reasons

    // First time rejecting without a reason; try rescheduling the slice.
    if (retry < retries)
    {
      debugging() && console.debug(`handleWorkReject: slice ${slice.identifier} is being retried.`);
      rejectedState.retry++;
      // Reset slice state to allow re-execution.
      jobManager.cloneSlice(slice);
      return false;
    }

    // Purge the job.
    const workRejectStatus = { message: reason, retries, errorCode: 'EWORKREJECT' };
    this.rejectJob(jobManager, workRejectStatus);
    // Tell everyone all about it, when allowed.
    if (jobManager.displayMaxDiagInfo)
    {
      const suffixMsg = 'All slices and sandboxes with the same jobId returned to the scheduler or terminated.';
      if (retries > 0)
        this.warning(`work.reject: The slice ${slice.identifier} has been retried ${retries} times and finally rejected with reason: ${reason}.`, suffixMsg);
      else
        this.warning(`work.reject: The slice ${slice.identifier} was rejected with reason: ${reason}.`, suffixMsg);
    }
    return true;
  }

  /**
   * Send payload to the workEmit endpoint in the event router.
   * @param {Slice}  slice
   * @param {string} eventName
   * @param {*}      payload
   * @param {string} [info]
   */
  postWorkEmit (slice, eventName, payload, info)
  {
    selectiveDebug2() && console.debug(`postWorkEmit: slice ${slice.identifier}, eventName ${eventName}, info ${info},`, payload);
    assert(slice);
    // Authorization should always be valid.
    if (!slice.authorizationMessage)
      this.warning(`postWorkEmit: missing authorization message for slice ${slice.identifier}`);
    else
    {
      const jobName = payload.jobName || slice.jobManager.public.name;
      if (eventName === 'error')
      {
        if (typeof payload === 'string')
          payload = { name: 'Error', message: payload, timestamp: new Date(), };
        else
        {
          const error = payload;
          // Do not report these errors.
          if (!Supervisor.isReportable(error))
            return;

          payload = {
            name: error.name,
            message: error.message,
            timestamp: error.timestamp ? error.timestamp : new Date(),
          };
          if (error.code)
          {
            payload['code'] = error.code;
            if (error.errorCode)
              payload['errorCode'] = error.errorCode;
          }
          else if (error.errorCode)
            payload['code'] = error.errorCode;
          if (error.stack)
            payload['stack'] = error.stack;
        }
      }
      if (info && !payload.info)
        payload.info = info;
      if (!payload.jobName && jobName)
        payload.jobName = jobName;
      const workEmitPayload = {
        eventName,
        payload,
        job: slice.jobId,
        slice: slice.sliceNumber,
        worker: this.workerId,
        authorizationMessage : slice.authorizationMessage,
      };
      selectiveDebug2() && console.debug(`postWorkEmit: slice ${slice.identifier}, eventName ${eventName}`, workEmitPayload);
      return this.dcp4.safeWorkEmit(workEmitPayload, `Failed to send workEmit (${eventName}) payload for slice ${slice.identifier}`)
        .then((success) => {
          if (!success)
            this.warning(`Message sent to workEmit is unauthorized; not accepted: payload=${utils.stringify(payload)}, slice=${slice.identifier}, event=${eventName}`);
        });
    }
  }

}
exports.Supervisor = Supervisor;
